
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { ChevronRight, ChevronDown, Plus, Edit3, Calendar, CheckSquare, Filter, Search, ArrowRight, ArrowLeft, Save, X, Building, Layers, MapPin, Wrench, FileText, ClipboardList, BarChart3, PanelRightClose, PanelRightOpen, Settings, GitBranch, Table, GanttChart, Network, Sparkles, AlertCircle, CheckCircle, Clock, Ban, Unlock, Loader2, Box, Users, Check, FolderKanban, CalendarDays, ListTodo, AlertTriangle, Trash2, Target, Flag, Folder, Trophy, Lock } from 'lucide-react';
import { SmartPlanningAssistant } from './planning/SmartPlanningAssistant';
import { ResourceGanttView } from './planning/ResourceGanttView';
import { ResourcePlannerModal } from './planning/ResourcePlannerModal';
import { groupTasksByActivity, calculateMasterPlanDelay, isWorkFrontReady } from './planning/planningUtils';
import { api, MasterData, MonthlyPlan, WeeklyPlan } from '../lib/api';
import { GanttChartView } from './planning/GanttChartView';
import { DependencyTreeView } from './planning/DependencyTreeView';
import { TeamManagementModal } from './TeamManagementModal';
import { MonthlyPlansView } from './planning/MonthlyPlansView';
import { ConcernsView } from './planning/ConcernsView';
import { RaiseConcernModal } from './planning/RaiseConcernModal';
import { WbsSyncControl } from './WbsSyncControl';
import MilestonesBrowsePanel from './planning/MilestonesBrowsePanel';
import type { MilestoneBrowseRow } from '../lib/api';
import {
  VirtualMonth,
  VirtualWeek,
  nextNMonths,
  nextNWeeks,
  weeksForMonth,
  nextAvailableWeekNumber,
  virtualMonthFor,
  mondayOf,
} from './planning/virtualPlans';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function personInitials(name?: string | null): string {
  if (!name) return '';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
import { Task, TeamMember, ProjectTreeNode as ApiTreeNode, TaskFilters, TreeNode } from './shared/types';
import {
  normalizeManpowerConstants,
  ManpowerConstant
} from './shared/manpowerUtils';
import {
  calculateTaskManpower,
  aggregateManpowerCosts
} from './shared/manpowerHelpers';
import {
  computeAutoGroupAxis,
  orderTasksByGroup,
} from './planning/taskGrouping';
import { getAllLaborRates } from './shared/manpowerConfig';
import { CONSTRUCTION_STAGES } from './shared/constants';
import { calculateVariance, getVarianceColor, formatDate } from './shared/utils';

// Helper to transform API task to frontend Task format
const transformApiTask = (apiTask: any): Task => ({
  id: String(apiTask.id),
  taskName: apiTask.task_name,
  category: apiTask.category_name || '',
  subCategory: apiTask.subcategory_name || '',
  building: apiTask.building_name || '',
  buildingId: apiTask.building_id || '',
  floor: apiTask.floor_name || '',
  area: '',
  unit: apiTask.unit_name || '',
  series: apiTask.series_name || '',
  taskUnit: apiTask.uom || '',
  quantity: parseFloat(apiTask.quantity) || 0,
  estimatedAmount: parseFloat(apiTask.estimated_cost) || 0,
  quantityCompleted: 0,
  actualCost: 0,
  status: apiTask.status === 'Not Started' ? 'Planned' :
    apiTask.status === 'In Progress' ? 'In Progress' :
      apiTask.status === 'Completed' ? 'Completed' : 'Planned',
  originalSchedule: {
    startDate: apiTask.planned_start_date || '',
    finishDate: apiTask.planned_finish_date || '',
    duration: 0
  },
  startDate: apiTask.planned_start_date || '',
  finishDate: apiTask.planned_finish_date || '',
  monthlyRevision: apiTask.monthly_plan_date ? {
    startDate: apiTask.monthly_plan_date,
    finishDate: apiTask.monthly_plan_date,
    reason: '',
    revisedBy: 'System',
    revisedAt: new Date().toISOString()
  } : undefined,
  weeklyRevision: apiTask.weekly_plan_date ? {
    startDate: apiTask.weekly_plan_date,
    finishDate: apiTask.weekly_plan_date,
    reason: '',
    notes: 'Planned for week',
    revisedBy: 'System',
    revisedAt: new Date().toISOString(),
    progressTarget: 0,
    quantityTarget: 0
  } : undefined,
  // Engineer assignment. The backend exposes a free-text `assigned_to`
  // (engineer name). Without this mapping, reloads always render the
  // task as unassigned in the Planning UI even if the DB has an engineer
  // on it — which is how the "not showing in Tasks view" bug began to
  // look like it was the Tasks view's fault.
  assignedTo: apiTask.assigned_to || undefined,
  // Plan assignment info
  activeMonthlyPlanId: apiTask.active_monthly_plan_id || null,
  activeWeeklyPlanId: apiTask.active_weekly_plan_id || null,
  planStatus: apiTask.plan_status || 'unplanned',
  activePlanMonth: apiTask.active_plan_month || null,
  activePlanYear: apiTask.active_plan_year || null,
  activePlanStatus: apiTask.active_plan_status || null,
  activeWeekNumber: apiTask.active_week_number || null,
  activeWeekName: apiTask.active_week_name || null,
  // Predecessor ids for this task. Sourced from the `task_dependencies` table
  // (initially populated by a stage-order template per unit; see
  // applyDependencyTemplate in backend/lib/wbs_sync.js). Drives Blocked Tasks,
  // Blockers, Open Workfronts, and the dependency tree view.
  dependencies: Array.isArray(apiTask.dependencies)
    ? apiTask.dependencies.map((d: unknown) => String(d))
    : [],
});

interface ConstructionPlanningInterfaceProps {
  // Global navigation and theme toggle now live in the persistent <AppSidebar>.
  // The view consumes global theme state via Tailwind's `dark:` variant, so no
  // explicit theme prop is needed here.
  theme?: 'light' | 'dark';
}

const ConstructionPlanningInterface: React.FC<ConstructionPlanningInterfaceProps> = () => {
  const [currentView, setCurrentView] = useState('master'); // master, monthly, weekly
  const [masterPlan, setMasterPlan] = useState<Task[]>([]);
  const [masterData, setMasterData] = useState<MasterData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Multi-project support
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [manpowerConstants, setManpowerConstants] = useState<ManpowerConstant[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  // Tree structure from API (Building → Category → Subcategory → Floor → Unit)
  const [projectTree, setProjectTree] = useState<ApiTreeNode[]>([]);

  // Selected node info for task loading
  const [selectedNodeFilters, setSelectedNodeFilters] = useState<TaskFilters | null>(null);

  // Load initial data (projects)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        const projectsData = await api.fetchProjects();
        setProjects(projectsData);

        if (projectsData.length > 0) {
          // Select Harita project by default, or fall back to first project
          const haritaProject = projectsData.find((p: any) => p.name === 'Harita');
          const defaultProject = haritaProject || projectsData[0];
          setSelectedProjectId(defaultProject.id);

          // Fetch Team Members for the default project
          try {
            const team = await api.fetchTeam(defaultProject.id);
            setTeamMembers(team);
          } catch (error) {
            console.error('Error loading team:', error);
            // Optionally set an error state for team loading specifically
          }
        } else {
          setIsLoading(false); // No projects to load
        }
      } catch (err) {
        console.error('Failed to load projects:', err);
        setError('Failed to load projects. Please ensure backend is running.');
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Load project tree and master data when project changes
  useEffect(() => {
    if (!selectedProjectId) return;

    const loadProjectData = async () => {
      try {
        setIsLoading(true);
        setMasterPlan([]); // Clear tasks while loading
        setSelectedNodeFilters(null);

        const [tree, data, constants] = await Promise.all([
          api.getProjectTree(selectedProjectId),
          api.getMasterData(selectedProjectId),
          api.getManpowerConstants(selectedProjectId)
        ]);

        setProjectTree(tree);
        setMasterData(data);
        setManpowerConstants(normalizeManpowerConstants(constants));
      } catch (err) {
        console.error('Failed to load project data:', err);
        setError('Failed to load project data.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProjectData();
  }, [selectedProjectId]);

  // Default the building filter to whichever building has the most recent task
  // activity (planned/actual dates on its tasks). Runs once per project load so
  // a re-sync or any other tree refresh doesn't override the user's choice.
  useEffect(() => {
    if (!selectedProjectId) return;
    if (projectTree.length === 0) return;
    if (autoSelectedBuildingForProjectRef.current === selectedProjectId) return;

    const buildingsWithActivity = projectTree
      .filter((b) => b.stats?.total > 0 && b.latestActivityDate)
      .sort((a, b) => (b.latestActivityDate || '').localeCompare(a.latestActivityDate || ''));

    if (buildingsWithActivity.length > 0) {
      const top = buildingsWithActivity[0];
      setSelectedBuilding(top.name);
      setBreadcrumb([top.name]);
    }
    autoSelectedBuildingForProjectRef.current = selectedProjectId;
  }, [projectTree, selectedProjectId]);

  // Load tasks when a node is selected (lazy loading)
  const loadTasksForNode = useCallback(async (filters: TaskFilters) => {
    try {
      setIsLoadingTasks(true);
      const tasks = await api.getTasksByNode(filters);
      console.log('[Debug] API tasks received:', tasks.length);
      const transformedTasks = tasks.map(transformApiTask);
      console.log('[Debug] Transformed tasks:', transformedTasks.length, transformedTasks[0]);
      setMasterPlan(transformedTasks);
      setSelectedNodeFilters(filters);
    } catch (err) {
      console.error('Failed to load tasks:', err);
      setError('Failed to load tasks for selected node.');
    } finally {
      setIsLoadingTasks(false);
    }
  }, []);

  // Cache of milestone_id -> fetched Task[]. We only refetch milestones
  // the user hasn't asked about yet; unchecking a milestone just drops
  // its tasks from masterPlan without hitting the server.
  const milestoneTaskCache = useRef(new Map<string, Task[]>());
  // Token of the last in-flight selection resolve. Prevents a stale
  // response from overwriting masterPlan if the user checks/unchecks
  // faster than the network.
  const milestoneLoadToken = useRef(0);

  // Hydrate masterPlan + selectedTasks from a set of checked milestones.
  // `tasks-by-node?milestone_id=` already exists (see backend/server.js);
  // we fan out in parallel and merge results, using a small cache so
  // repeated toggles don't re-fetch.
  const loadTasksForMilestones = useCallback(async (
    milestoneIds: string[],
    leafTaskIds: string[],
    milestoneRows: MilestoneBrowseRow[],
  ) => {
    if (!selectedProjectId || milestoneIds.length === 0) return;
    const token = ++milestoneLoadToken.current;
    try {
      setIsLoadingTasks(true);
      const missing = milestoneIds.filter((id) => !milestoneTaskCache.current.has(id));
      if (missing.length > 0) {
        const fetched = await Promise.all(
          missing.map((id) =>
            api.getTasksByNode({ project_id: selectedProjectId, milestone_id: id })
              .then((rows) => ({ id, tasks: rows.map(transformApiTask) }))
              .catch((err) => {
                console.error(`Failed to load tasks for milestone ${id}:`, err);
                return { id, tasks: [] as Task[] };
              })
          )
        );
        for (const { id, tasks } of fetched) {
          milestoneTaskCache.current.set(id, tasks);
        }
      }

      // Stale check: if the user kept clicking, a newer resolve is in
      // flight — abandon this one so we don't clobber its result.
      if (token !== milestoneLoadToken.current) return;

      const byId = new Map<string, Task>();
      for (const id of milestoneIds) {
        const tasks = milestoneTaskCache.current.get(id) || [];
        for (const t of tasks) byId.set(t.id, t);
      }
      const merged = Array.from(byId.values());
      setMasterPlan(merged);
      setSelectedNodeFilters({ project_id: selectedProjectId });
      const selectable = merged.length > 0
        ? merged.map((t) => t.id)
        : leafTaskIds;
      setSelectedTasks(selectable);
      setSelectedNode('milestones:selected');
      setBreadcrumb(['Milestones', `${milestoneRows.length} selected`]);
      setLoadedMilestoneSummary({
        milestoneIds,
        names: milestoneRows.map((r) => r.name),
      });
    } catch (err) {
      console.error('Failed to hydrate tasks for milestones:', err);
      setError('Failed to load tasks for the selected milestones.');
    } finally {
      if (token === milestoneLoadToken.current) setIsLoadingTasks(false);
    }
  }, [selectedProjectId]);

  // Wires the milestone panel's live selection into the task table.
  // Debounced so rapid checkbox toggling doesn't flood the API.
  const milestoneDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleMilestoneSelectionChange = useCallback((payload: {
    milestoneIds: string[];
    leafTaskIds: string[];
    milestoneRows: MilestoneBrowseRow[];
  }) => {
    if (milestoneDebounceRef.current) clearTimeout(milestoneDebounceRef.current);
    if (payload.milestoneIds.length === 0) {
      // Fast path — clear everything without waiting for the debounce tick.
      milestoneDebounceRef.current = null;
      milestoneLoadToken.current += 1;
      setMasterPlan([]);
      setSelectedTasks([]);
      setLoadedMilestoneSummary(null);
      setSelectedNode('root');
      setBreadcrumb(['Milestones']);
      return;
    }
    milestoneDebounceRef.current = setTimeout(() => {
      void loadTasksForMilestones(payload.milestoneIds, payload.leafTaskIds, payload.milestoneRows);
    }, 250);
  }, [loadTasksForMilestones]);

  const [monthlyPlan, setMonthlyPlan] = useState<Task[]>([]);
  const [weeklyPlan, setWeeklyPlan] = useState<Task[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [selectedNode, setSelectedNode] = useState('root'); // Controls what's shown in table
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({ 'root': true });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterEngineer, setFilterEngineer] = useState('All');
  const [filterContractor, setFilterContractor] = useState('All');
  const [filterFloor, setFilterFloor] = useState('All');
  const [filterSeries, setFilterSeries] = useState('All');
  const [filterUnit, setFilterUnit] = useState('All');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('All');
  const [breadcrumb, setBreadcrumb] = useState(['All Buildings']);

  // Tracks whether we've already defaulted the building filter for the current
  // project. We auto-pick the building with the most recent task activity on
  // first tree load, but only until the user makes their own choice.
  const autoSelectedBuildingForProjectRef = useRef<string | null>(null);

  // Refetch the project tree with the active quick-filters so the left-side
  // building/category counts track what the main panel is showing. Debounced
  // so typing into a date input doesn't fire a request per keystroke. Runs
  // after the initial project load and whenever the relevant filters change.
  useEffect(() => {
    if (!selectedProjectId) return;
    const anyFilterActive =
      Boolean(filterDateStart) || Boolean(filterDateEnd) || filterStatus === 'Exclude Completed';

    const handle = setTimeout(async () => {
      try {
        const tree = await api.getProjectTree(
          selectedProjectId,
          anyFilterActive
            ? {
                dateStart: filterDateStart || undefined,
                dateEnd: filterDateEnd || undefined,
                excludeCompleted: filterStatus === 'Exclude Completed',
              }
            : undefined,
        );
        setProjectTree(tree);
      } catch (err) {
        console.error('Failed to refresh filtered project tree:', err);
      }
    }, 250);

    return () => clearTimeout(handle);
  }, [selectedProjectId, filterDateStart, filterDateEnd, filterStatus]);

  // PHASE 1: Smart Planning Assistant - Filter States
  const [showOverdueFromMaster, setShowOverdueFromMaster] = useState(false);
  const [showReadyToStart, setShowReadyToStart] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [quickProgressTask, setQuickProgressTask] = useState(null);

  const [showMaterialsModal, setShowMaterialsModal] = useState(false);
  const [resourceTab, setResourceTab] = useState('materials'); // 'materials' or 'labour'
  const [labourViewMode, setLabourViewMode] = useState('detailed'); // 'detailed' | 'standard'
  const [taskResources, setTaskResources] = useState<any[]>([]);
  const [isLoadingResources, setIsLoadingResources] = useState(false);

  // Team Management Modal
  const [showTeamModal, setShowTeamModal] = useState(false);

  // Assignment Dock (for Weekly Plan)
  const [assignmentSearchTerm, setAssignmentSearchTerm] = useState('');

  // Fetch task resources when modal is opened
  useEffect(() => {
    if (showMaterialsModal && selectedTasks.length > 0) {
      const fetchResources = async () => {
        setIsLoadingResources(true);
        try {
          const resources = await api.getTaskResources(selectedTasks);
          setTaskResources(resources);
        } catch (err) {
          console.error('Failed to fetch task resources:', err);
          setTaskResources([]);
        } finally {
          setIsLoadingResources(false);
        }
      };
      fetchResources();
    }
  }, [showMaterialsModal, selectedTasks]);

  // Initialize monthly and weekly plans from master plan data
  useEffect(() => {
    if (masterPlan.length > 0) {
      // populate monthly plan
      const monthly = masterPlan.filter(t => t.monthlyRevision);
      setMonthlyPlan(monthly);

      // populate weekly plan
      const weekly = masterPlan.filter(t => t.weeklyRevision);
      setWeeklyPlan(weekly);
    }
  }, [masterPlan]);

  const [viewMode, setViewMode] = useState('table'); // 'table', 'gantt', or 'dependency'
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [viewDensity, setViewDensity] = useState('normal'); // 'normal' or 'compact'
  const [showSmartPlanningModal, setShowSmartPlanningModal] = useState(false);
  const [showResourceTimelineModal, setShowResourceTimelineModal] = useState(false);
  const [showResourcePlannerModal, setShowResourcePlannerModal] = useState(false);
  const [showMonthlyPlansModal, setShowMonthlyPlansModal] = useState(false);
  const [showConcernsModal, setShowConcernsModal] = useState(false);
  const [showRaiseConcernModal, setShowRaiseConcernModal] = useState(false);
  const [concernPreSelectedTasks, setConcernPreSelectedTasks] = useState<number[]>([]);
  const [planningStep, setPlanningStep] = useState(1);
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
  const [isSavingPlan, setIsSavingPlan] = useState(false);

  // Plan Management State
  const [monthlyPlans, setMonthlyPlans] = useState<MonthlyPlan[]>([]);
  const [weeklyPlans, setWeeklyPlans] = useState<WeeklyPlan[]>([]);
  /**
   * All weekly plans in this project, across every month. Used to decide
   * whether a "next 6 weeks" row should render as a real plan (DB-backed)
   * or as a virtual placeholder. Kept in sync with per-month `weeklyPlans`
   * via `refreshProjectWeeklyPlans()`.
   */
  const [projectWeeklyPlans, setProjectWeeklyPlans] = useState<WeeklyPlan[]>([]);
  const [selectedMonthlyPlan, setSelectedMonthlyPlan] = useState<MonthlyPlan | null>(null);
  const [selectedWeeklyPlan, setSelectedWeeklyPlan] = useState<WeeklyPlan | null>(null);
  /**
   * True while we're auto-creating a monthly or weekly plan from a virtual
   * dropdown row. Blocks double-submit and shows a spinner next to the row.
   */
  const [materializingKey, setMaterializingKey] = useState<string | null>(null);
  const [showMonthlyDropdown, setShowMonthlyDropdown] = useState(false);
  const [showWeeklyDropdown, setShowWeeklyDropdown] = useState(false);
  const [showAddToPlanDropdown, setShowAddToPlanDropdown] = useState(false);
  const [showMoveToWeekDropdown, setShowMoveToWeekDropdown] = useState(false);
  const [isAddingToPlan, setIsAddingToPlan] = useState(false);
  const [showCreateWeeklyModal, setShowCreateWeeklyModal] = useState(false);
  const [newWeeklyPlan, setNewWeeklyPlan] = useState({
    week_number: 1,
    start_date: '',
    end_date: '',
    name: ''
  });
  const [isCreatingWeekly, setIsCreatingWeekly] = useState(false);
  
  // Tasks specific to selected plans (fetched from DB)
  const [monthlyPlanTasks, setMonthlyPlanTasks] = useState<Task[]>([]);
  const [weeklyPlanTasks, setWeeklyPlanTasks] = useState<Task[]>([]);
  const [isLoadingPlanTasks, setIsLoadingPlanTasks] = useState(false);

  // Load monthly plans + project-wide weekly plans when project changes
  useEffect(() => {
    if (!selectedProjectId) return;

    const loadPlans = async () => {
      try {
        const [plans, projectWeeks] = await Promise.all([
          api.getMonthlyPlans(selectedProjectId),
          api.getProjectWeeklyPlans(selectedProjectId).catch(() => [] as WeeklyPlan[]),
        ]);
        setMonthlyPlans(plans);
        setProjectWeeklyPlans(projectWeeks);

        // Auto-select the most appropriate plan (upcoming or current month)
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;

        const upcomingPlans = plans.filter(p =>
          (p.year > currentYear || (p.year === currentYear && p.month >= currentMonth)) &&
          p.status !== 'closed'
        ).sort((a, b) => a.year === b.year ? a.month - b.month : a.year - b.year);

        if (upcomingPlans.length > 0) {
          setSelectedMonthlyPlan(upcomingPlans[0]);
        } else if (plans.length > 0) {
          setSelectedMonthlyPlan(plans[0]);
        }
      } catch (err) {
        console.error('Failed to load monthly plans:', err);
      }
    };

    loadPlans();
  }, [selectedProjectId]);

  /**
   * Pull the project's full weekly-plan list back from the API. Called after
   * we materialize a virtual week so the dropdown's virtual/real merge
   * immediately reflects the new row.
   */
  const refreshProjectWeeklyPlans = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      const weeks = await api.getProjectWeeklyPlans(selectedProjectId);
      setProjectWeeklyPlans(weeks);
    } catch (err) {
      console.error('Failed to refresh project weekly plans:', err);
    }
  }, [selectedProjectId]);

  /**
   * Delete a draft weekly plan. Backend rejects anything other than
   * `status='draft'` with a 400, but we also defend here so the user
   * never sees the trash icon on a published week in the first place.
   *
   * Side-effects:
   *  - `weekly_plan_tasks` cascade-delete on the server (schema FK).
   *  - If the deleted week was the current selection, we clear it.
   *  - Refreshes both scoped and project-wide weekly caches so every
   *    dropdown that references it updates in the same tick.
   */
  const deleteWeeklyPlan = useCallback(async (week: WeeklyPlan) => {
    if (week.status !== 'draft') {
      alert(`Cannot delete Week ${week.week_number}: it is ${week.status}.`);
      return;
    }
    const confirmed = window.confirm(
      `Delete Week ${week.week_number} (${week.start_date} to ${week.end_date})?\n\n` +
      `This will remove the week and any tasks assigned to it. ` +
      `Tasks will remain in the monthly plan.`
    );
    if (!confirmed) return;

    try {
      await api.deleteWeeklyPlan(week.id);
      if (selectedWeeklyPlan?.id === week.id) {
        setSelectedWeeklyPlan(null);
        if (currentView === 'weekly') setCurrentView('master');
      }
      if (selectedMonthlyPlan && week.monthly_plan_id === selectedMonthlyPlan.id) {
        const refreshed = await api.getWeeklyPlans(selectedMonthlyPlan.id);
        setWeeklyPlans(refreshed);
      }
      await refreshProjectWeeklyPlans();
    } catch (err: any) {
      alert(err?.message || 'Failed to delete weekly plan');
    }
  }, [selectedWeeklyPlan, selectedMonthlyPlan, currentView, refreshProjectWeeklyPlans]);

  /**
   * Materialize a virtual month into a real `monthly_plans` row and select it.
   * No-op if a real plan for that year/month already exists (we just select it).
   */
  const materializeMonth = useCallback(async (vm: VirtualMonth) => {
    if (!selectedProjectId) return;
    const existing = monthlyPlans.find(p => p.year === vm.year && p.month === vm.month);
    if (existing) {
      setSelectedMonthlyPlan(existing);
      setCurrentView('monthly');
      return;
    }
    setMaterializingKey(vm.key);
    try {
      const created = await api.createMonthlyPlan(selectedProjectId, {
        year: vm.year,
        month: vm.month,
        name: vm.name,
      });
      const refreshed = await api.getMonthlyPlans(selectedProjectId);
      setMonthlyPlans(refreshed);
      const toSelect = refreshed.find(p => p.id === created.id) || created;
      setSelectedMonthlyPlan(toSelect);
      setCurrentView('monthly');
    } catch (err: any) {
      alert(err?.message || 'Failed to create monthly plan');
    } finally {
      setMaterializingKey(null);
    }
  }, [selectedProjectId, monthlyPlans]);

  /**
   * Materialize a virtual week. Creates the owning monthly plan first if
   * needed, resolves a non-conflicting `week_number` within that month,
   * creates the weekly plan, then selects it.
   */
  const materializeWeek = useCallback(async (vw: VirtualWeek) => {
    if (!selectedProjectId) return;
    setMaterializingKey(vw.key);
    try {
      // Ensure the owning monthly plan exists.
      let owningMonth = monthlyPlans.find(p => p.year === vw.year && p.month === vw.month) || null;
      let allMonths = monthlyPlans;
      if (!owningMonth) {
        const vm = virtualMonthFor(vw.year, vw.month);
        await api.createMonthlyPlan(selectedProjectId, {
          year: vm.year,
          month: vm.month,
          name: vm.name,
        });
        allMonths = await api.getMonthlyPlans(selectedProjectId);
        setMonthlyPlans(allMonths);
        owningMonth = allMonths.find(p => p.year === vw.year && p.month === vw.month) || null;
      }
      if (!owningMonth) throw new Error('Could not resolve owning monthly plan');

      // Fetch the real weeks already in this month so we can pick a free
      // week_number and avoid the UNIQUE(monthly_plan_id, week_number) violation.
      const existingWeeks = await api.getWeeklyPlans(owningMonth.id);
      const alreadyExists = existingWeeks.find(w => w.start_date === vw.startDate);
      let created: WeeklyPlan;
      if (alreadyExists) {
        created = alreadyExists;
      } else {
        const weekNumber = nextAvailableWeekNumber(
          existingWeeks.map(w => w.week_number),
          vw.weekNumber,
        );
        created = await api.createWeeklyPlan({
          monthly_plan_id: owningMonth.id,
          week_number: weekNumber,
          start_date: vw.startDate,
          end_date: vw.endDate,
          name: `Week ${weekNumber}`,
        });
      }

      setSelectedMonthlyPlan(owningMonth);
      const refreshedWeeks = await api.getWeeklyPlans(owningMonth.id);
      setWeeklyPlans(refreshedWeeks);
      setSelectedWeeklyPlan(refreshedWeeks.find(w => w.id === created.id) || created);
      setCurrentView('weekly');
      await refreshProjectWeeklyPlans();
    } catch (err: any) {
      alert(err?.message || 'Failed to create weekly plan');
    } finally {
      setMaterializingKey(null);
    }
  }, [selectedProjectId, monthlyPlans, refreshProjectWeeklyPlans]);

  // Load weekly plans when monthly plan is selected
  useEffect(() => {
    if (!selectedMonthlyPlan) {
      setWeeklyPlans([]);
      setSelectedWeeklyPlan(null);
      return;
    }

    const loadWeeklyPlans = async () => {
      try {
        const weeks = await api.getWeeklyPlans(selectedMonthlyPlan.id);
        setWeeklyPlans(weeks);
        
        // Auto-select the upcoming week
        const now = new Date();
        const upcomingWeeks = weeks.filter(w => new Date(w.end_date) >= now)
          .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
        
        if (upcomingWeeks.length > 0) {
          setSelectedWeeklyPlan(upcomingWeeks[0]);
        } else if (weeks.length > 0) {
          setSelectedWeeklyPlan(weeks[0]);
        }
      } catch (err) {
        console.error('Failed to load weekly plans:', err);
      }
    };

    loadWeeklyPlans();
  }, [selectedMonthlyPlan]);

  // Load tasks for selected monthly plan when viewing monthly
  useEffect(() => {
    if (currentView !== 'monthly' || !selectedMonthlyPlan) {
      setMonthlyPlanTasks([]);
      return;
    }

    const loadMonthlyPlanTasks = async () => {
      try {
        setIsLoadingPlanTasks(true);
        const planDetail = await api.getMonthlyPlan(selectedMonthlyPlan.id);
        
        // Transform plan tasks to Task format
        const transformedTasks: Task[] = planDetail.tasks.map((pt: any) => ({
          id: String(pt.task_id),
          taskName: pt.task_name,
          category: pt.category_name || '',
          subCategory: '',
          building: pt.building_name || '',
          buildingId: '',
          floor: pt.floor_name || '',
          area: '',
          unit: pt.unit_name || '',
          series: '',
          taskUnit: pt.uom || '',
          quantity: parseFloat(pt.quantity) || 0,
          estimatedAmount: 0,
          quantityCompleted: 0,
          actualCost: 0,
          status: pt.status === 'completed' ? 'Completed' : 
                  pt.status === 'active' ? 'In Progress' : 'Planned',
          originalSchedule: { startDate: '', finishDate: '', duration: 0 },
          startDate: pt.planned_start_date || '',
          finishDate: pt.planned_finish_date || '',
          // Plan-specific fields
          planTaskId: pt.id,
          planSource: pt.source,
          planStatus: pt.status,
          startProgress: pt.start_progress,
          endProgress: pt.end_progress,
          assignedTo: pt.assigned_to || undefined,
        }));
        
        setMonthlyPlanTasks(transformedTasks);
      } catch (err) {
        console.error('Failed to load monthly plan tasks:', err);
        setMonthlyPlanTasks([]);
      } finally {
        setIsLoadingPlanTasks(false);
      }
    };

    loadMonthlyPlanTasks();
  }, [currentView, selectedMonthlyPlan]);

  // Load tasks for selected weekly plan when viewing weekly
  useEffect(() => {
    if (currentView !== 'weekly' || !selectedWeeklyPlan) {
      setWeeklyPlanTasks([]);
      return;
    }

    const loadWeeklyPlanTasks = async () => {
      try {
        setIsLoadingPlanTasks(true);
        const planDetail = await api.getWeeklyPlanDetail(selectedWeeklyPlan.id);
        
        // Transform plan tasks to Task format
        const transformedTasks: Task[] = planDetail.tasks.map((wpt: any) => ({
          id: String(wpt.task_id),
          taskName: wpt.task_name,
          category: wpt.category_name || '',
          subCategory: '',
          building: wpt.building_name || '',
          buildingId: '',
          floor: wpt.floor_name || '',
          area: '',
          unit: wpt.unit_name || '',
          series: '',
          taskUnit: wpt.uom || '',
          quantity: parseFloat(wpt.quantity) || 0,
          estimatedAmount: parseFloat(wpt.estimated_cost) || 0,
          quantityCompleted: 0,
          actualCost: 0,
          status: wpt.weekly_status === 'completed' ? 'Completed' : 
                  wpt.weekly_status === 'active' ? 'In Progress' : 'Planned',
          originalSchedule: { startDate: '', finishDate: '', duration: 0 },
          startDate: wpt.planned_start_date || '',
          finishDate: wpt.planned_finish_date || '',
          // Weekly plan specific fields
          weeklyPlanTaskId: wpt.weekly_plan_task_id,
          monthlyPlanTaskId: wpt.monthly_plan_task_id,
          weeklyStatus: wpt.weekly_status,
          monthlyStatus: wpt.monthly_status,
          assignedTo: wpt.assigned_to || undefined,
        }));
        
        setWeeklyPlanTasks(transformedTasks);
      } catch (err) {
        console.error('Failed to load weekly plan tasks:', err);
        setWeeklyPlanTasks([]);
      } finally {
        setIsLoadingPlanTasks(false);
      }
    };

    loadWeeklyPlanTasks();
  }, [currentView, selectedWeeklyPlan]);

  // Left panel view mode:
  //   'location'   — the category/building/floor tree (default)
  //   'milestones' — the schedule-bucketed milestone browser used to
  //                  bulk-select leaf tasks by milestone for monthly/weekly
  //                  plan assignment.
  const [leftPanelMode, setLeftPanelMode] = useState<'location' | 'milestones'>('location');
  // Names of milestones the user last loaded into selection, shown as a
  // breadcrumb-style banner above the task table so they can see what
  // pool they're currently acting on.
  const [loadedMilestoneSummary, setLoadedMilestoneSummary] = useState<
    { milestoneIds: string[]; names: string[] } | null
  >(null);

  // Resizable left panel
  const [leftPanelWidth, setLeftPanelWidth] = useState(320); // Default 320px (w-80)
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  // Handle panel resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = leftPanelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [leftPanelWidth]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      const diff = e.clientX - startX.current;
      const newWidth = Math.min(Math.max(startWidth.current + diff, 200), 600); // Min 200px, Max 600px
      setLeftPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);
  // Smart Planning Assistant state moved to component
  const [visibleColumns, setVisibleColumns] = useState({
    task: true,
    stage: true,
    floor: true,
    // `locationUnit` renders task.unit (e.g. "A101"); unit (below) renders
    // task.taskUnit (e.g. "sqm") — kept separate because they are different
    // concepts despite the legacy naming collision.
    locationUnit: true,
    series: true,
    unit: true,
    status: true,
    progress: true,
    cost: true,
    schedule: true,
    target: true,
    delay: true,
    dependencies: true, // Show dependency blocking & trigger analysis
    revision: false, // Historical - hide by default
    assigned: true
  });

  // Tree structure now comes from API (projectTree state)
  // Filter by selected building if needed
  const filteredTree = useMemo(() => {
    if (selectedBuilding === 'All') {
      return projectTree;
    }
    return projectTree.filter(building => building.name === selectedBuilding);
  }, [projectTree, selectedBuilding]);

  const getCurrentTasks = () => {
    switch (currentView) {
      case 'master': return masterPlan;
      case 'monthly': return monthlyPlanTasks; // Tasks assigned to selected monthly plan
      case 'weekly': return weeklyPlanTasks; // Tasks assigned to selected weekly plan
      default: return [];
    }
  };

  // Reset the tree to a collapsed state whenever the project/building filter
  // changes. Only the synthetic root node stays open; every building and
  // category under it starts closed so the user gets a clean, scannable list
  // and drills in explicitly by clicking a building.
  useEffect(() => {
    setExpandedNodes({ root: true });
  }, [filteredTree]);

  // Get filtered tasks - tasks are now loaded lazily per selected node
  const getFilteredTasksForTable = () => {
    // Tasks are now filtered on the backend, just return them
    return getCurrentTasks();
  };

  const toggleNodeExpansion = (nodeKey: string) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeKey]: !prev[nodeKey]
    }));
  };

  // Select a node and load its tasks lazily.
  const selectNode = (nodeKey: string, nodePath: string[], filters: TaskFilters) => {
    setSelectedNode(nodeKey);
    setBreadcrumb(nodePath);

    if (selectedProjectId) {
      loadTasksForNode(filters);
    }
  };

  // Editing functions
  const startEditing = (task) => {
    setEditingTask(task.id);
    setEditFormData({
      monthlyStartDate: task.monthlyRevision?.startDate || '',
      monthlyFinishDate: task.monthlyRevision?.finishDate || '',
      monthlyReason: task.monthlyRevision?.reason || '',
      weeklyStartDate: task.weeklyRevision?.startDate || '',
      weeklyFinishDate: task.weeklyRevision?.finishDate || '',
      weeklyReason: task.weeklyRevision?.reason || '',
      weeklyNotes: task.weeklyRevision?.notes || '',
      assignedTo: task.assignedTo,
      contractor: task.contractor
    });
  };

  const cancelEditing = () => {
    setEditingTask(null);
    setEditFormData({});
  };

  const saveEditing = () => {
    // Here you would typically save to backend
    console.log('Saving task:', editingTask, editFormData);

    // For now, just close the editing mode
    setEditingTask(null);
    setEditFormData({});

    // In a real app, you'd update the task data and refresh the view
  };

  const removeFromPlan = (taskId: string) => {
    const task = masterPlan.find(t => t.id === taskId); // Find full task details

    if (currentView === 'monthly') {
      // Constraint: Cannot remove from Monthly if added to Weekly
      if (task?.weeklyRevision) {
        alert(`Cannot remove "${task.taskName}" from Monthly Plan because it is already scheduled in the Weekly Plan (starting ${formatDate(task.weeklyRevision.startDate)}). Please remove it from the Weekly Plan first.`);
        return;
      }
      setMonthlyPlan(prev => prev.filter(t => t.id !== taskId));
    } else if (currentView === 'weekly') {
      setWeeklyPlan(prev => prev.filter(t => t.id !== taskId));
    }
  };

  const savePlan = async () => {
    try {
      setIsSavingPlan(true);

      const tasksToSave = currentView === 'monthly' ? monthlyPlan : weeklyPlan;
      const currentPlanIds = tasksToSave.map(t => t.id);

      // Determine tasks that were removed (were in masterPlan with revision, but not in current list)
      const tasksToRemoveIds = masterPlan
        .filter(t => {
          if (currentView === 'monthly') return t.monthlyRevision && !currentPlanIds.includes(t.id);
          if (currentView === 'weekly') return t.weeklyRevision && !currentPlanIds.includes(t.id);
          return false;
        })
        .map(t => t.id);

      // Use first day of current month for monthly, start of current week for weekly
      const today = new Date();
      const monthlyDate = currentView === 'monthly'
        ? new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0]
        : undefined;

      // For weekly, simplistic "today" for now, or could calculate start of week
      const weeklyDate = currentView === 'weekly'
        ? today.toISOString().split('T')[0]
        : undefined;

      // 1. Update tasks currently in the plan
      if (currentPlanIds.length > 0) {
        await api.updateTaskPlan(currentPlanIds, monthlyDate, weeklyDate);
      }

      // 2. Clear tasks that were removed (set relevant date to null)
      if (tasksToRemoveIds.length > 0) {
        if (currentView === 'monthly') {
          // Set monthly date to null. We preserve weeklyDate (pass undefined) so it's not touched?
          // Actually updateTaskPlan with undefined skips the field.
          await api.updateTaskPlan(tasksToRemoveIds, null, undefined);
        } else {
          // Set weekly date to null
          await api.updateTaskPlan(tasksToRemoveIds, undefined, null);
        }
      }

      setIsSavingPlan(false);
      alert(`${currentView === 'monthly' ? 'Monthly' : 'Weekly'} plan saved successfully!`);

      // Refresh data to reflect changes in Master Plan
      if (selectedNodeFilters) {
        loadTasksForNode(selectedNodeFilters);
      }
    } catch (err) {
      console.error('Failed to save plan:', err);
      setIsSavingPlan(false);
      alert('Failed to save plan.');
    }
  };

  const updateEditFormData = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Function to refresh team members
  const refreshTeamData = async () => {
    try {
      if (selectedProjectId) {
        const team = await api.fetchTeam(selectedProjectId);
        setTeamMembers(team);
      }
    } catch (error) {
      console.error('Error refreshing team:', error);
    }
  };

  // Assignment Dock helpers
  const getEngineerStats = (engineerName: string) => {
    const assignedTasks = weeklyPlan.filter(t => t.assignedTo === engineerName);
    const taskCount = assignedTasks.length;
    // Calculate load as percentage (assume max 20 tasks = 100%)
    const maxTasks = 20;
    const load = Math.min(Math.round((taskCount / maxTasks) * 100), 100);
    return { taskCount, load };
  };

  const getEngineerAvailability = (engineerName: string) => {
    const { load } = getEngineerStats(engineerName);
    if (load >= 90) return 'BUSY';
    if (load >= 70) return 'MODERATE';
    return 'AVAILABLE';
  };

  const assignTasksToEngineer = async (engineerName: string) => {
    if (selectedTasks.length === 0) return;

    // Resolve the engineer name back to a team_members.id so we can persist
    // the FK (assigned_team_member_id). Without this the admin Tasks view
    // — which reads mobile_task_view.assigned_team_member_id — never sees
    // the assignment.
    const member = teamMembers.find(m => m.name === engineerName);
    if (!member) {
      alert(`Engineer "${engineerName}" is not in this project's team list.`);
      return;
    }

    try {
      await api.assignTasksToMember(selectedTasks, member.id);
    } catch (err) {
      console.error('Failed to persist engineer assignment', err);
      alert('Could not save the assignment. Please retry.');
      return;
    }

    // Only apply the optimistic state update after the API accepted the write.
    // We have to touch every list that the view renders from: the Planning
    // table reads from weeklyPlanTasks / monthlyPlanTasks (see
    // getCurrentTasks), while getEngineerStats / filteredEngineers read from
    // weeklyPlan. Keeping them in sync is tedious but correct until these
    // arrays are consolidated.
    const applyAssignment = (tasks: Task[]): Task[] =>
      tasks.map(t =>
        selectedTasks.includes(t.id) ? { ...t, assignedTo: engineerName } : t,
      );

    setWeeklyPlanTasks(applyAssignment);
    setMonthlyPlanTasks(applyAssignment);
    setWeeklyPlan(applyAssignment);
    setMonthlyPlan(applyAssignment);
    setMasterPlan(applyAssignment);

    setSelectedTasks([]);
  };

  const filteredEngineers = teamMembers.filter(member =>
    member.name.toLowerCase().includes(assignmentSearchTerm.toLowerCase()) ||
    member.role.toLowerCase().includes(assignmentSearchTerm.toLowerCase())
  );

  // --- View Helpers ---
  // Placeholder for "Manage Team" button in the toolbar (assuming a toolbar exists in the JSX)
  // <button onClick={() => setShowTeamModal(true)} className="btn">Manage Team</button>

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filterStage !== 'All') count++;
    if (filterStatus !== 'All') count++;
    if (filterEngineer !== 'All') count++;
    if (filterContractor !== 'All') count++;
    if (filterFloor !== 'All') count++;
    if (filterSeries !== 'All') count++;
    if (filterUnit !== 'All') count++;
    if (filterDateStart || filterDateEnd) count++;
    if (searchTerm) count++;
    return count;
  };

  const clearAllFilters = () => {
    setFilterStage('All');
    setFilterStatus('All');
    setFilterEngineer('All');
    setFilterContractor('All');
    setFilterFloor('All');
    setFilterSeries('All');
    setFilterUnit('All');
    setFilterDateStart('');
    setFilterDateEnd('');
    setSearchTerm('');
  };

  // Aggregate materials from selected tasks
  const getAggregatedMaterials = () => {
    // Use the fetched taskResources instead of task.materials
    const materialsMap: Record<string, any> = {};

    taskResources.filter(r => r.resource_type === 'Material').forEach(material => {
      const key = `${material.resource_name}| ${material.unit} `;

      if (!materialsMap[key]) {
        materialsMap[key] = {
          category: 'Material', // We can group simply by 'Material' or by task category if available
          name: material.resource_name,
          unit: material.unit,
          quantity: 0,
          rate: parseFloat(material.rate) || 0,
          total: 0
        };
      }

      materialsMap[key].quantity += parseFloat(material.estimated_quantity) || 0;
      materialsMap[key].total += parseFloat(material.estimated_amount) || 0;
    });

    // Group by category (putting all in 'Materials' for now as DB doesn't have material category)
    const grouped = { 'Materials': Object.values(materialsMap) };
    return grouped;
  };

  // Aggregate labour from selected tasks
  const getAggregatedLabour = () => {
    // Use the fetched taskResources instead of task.labour
    const labourMap: Record<string, any> = {};

    taskResources.filter(r => r.resource_type === 'Labour').forEach(labour => {
      const key = `${labour.resource_name}| ${labour.unit} `;

      if (!labourMap[key]) {
        labourMap[key] = {
          category: 'Labour',
          name: labour.resource_name,
          unit: labour.unit,
          quantity: 0,
          rate: parseFloat(labour.rate) || 0,
          total: 0
        };
      }

      labourMap[key].quantity += parseFloat(labour.estimated_quantity) || 0;
      labourMap[key].total += parseFloat(labour.estimated_amount) || 0;
    });

    // Group by category
    const grouped = { 'Labour': Object.values(labourMap) };
    return grouped;
  };

  // Aggregate manpower from selected tasks using new constants system
  const aggregatedManpower = useMemo(() => {
    const currentTasks = getCurrentTasks();
    const selectedTasksList = currentTasks.filter(task => selectedTasks.includes(task.id));

    // Debug logging
    console.log('[Manpower Debug] Selected tasks:', selectedTasksList.length);
    console.log('[Manpower Debug] Manpower constants:', manpowerConstants.length);
    if (selectedTasksList.length > 0 && manpowerConstants.length > 0) {
      const sampleTask = selectedTasksList[0];
      console.log('[Manpower Debug] Sample task:', {
        category: sampleTask.category,
        subCategory: sampleTask.subCategory,
        quantity: sampleTask.quantity,
        taskUnit: sampleTask.taskUnit
      });
      console.log('[Manpower Debug] Sample constant:', manpowerConstants[0]);
    }

    // Calculate manpower for each task if not already calculated
    const tasksWithManpower = selectedTasksList.map(task => {
      if (!task.manpower) {
        const manpower = calculateTaskManpower(task, manpowerConstants);
        if (!manpower) {
          console.log('[Manpower Debug] No manpower for task:', task.category, '-', task.subCategory);
        }
        return { ...task, manpower };
      }
      return task;
    });

    // Filter tasks that have manpower data
    const tasksWithValidManpower = tasksWithManpower.filter(task => task.manpower);
    console.log('[Manpower Debug] Tasks with valid manpower:', tasksWithValidManpower.length);

    // Aggregate by labor type and category
    const laborRates = getAllLaborRates();
    const manpowerByCategory = {};
    const totalByLabor = {};

    tasksWithValidManpower.forEach(task => {
      const category = task.category;

      if (!manpowerByCategory[category]) {
        manpowerByCategory[category] = {};
      }

      task.manpower.requirements.forEach((req, idx) => {
        const labour = req.labour;
        const cost = task.manpower.costs[idx];

        // Aggregate by category and labor type
        if (!manpowerByCategory[category][labour]) {
          manpowerByCategory[category][labour] = {
            labour,
            days: 0,
            rate: cost.dailyRate,
            total: 0
          };
        }
        manpowerByCategory[category][labour].days += req.daysRequired;
        manpowerByCategory[category][labour].total += cost.totalCost;

        // Track total across all categories
        if (!totalByLabor[labour]) {
          totalByLabor[labour] = {
            labour,
            days: 0,
            rate: 0, // Rate might vary, so we don't aggregate it here
            total: 0
          };
        }
        totalByLabor[labour].days += req.daysRequired;
        totalByLabor[labour].total += cost.totalCost;
      });
    });

    // Convert to array format for display
    const resultByCategory = {};
    Object.keys(manpowerByCategory).forEach(category => {
      resultByCategory[category] = Object.values(manpowerByCategory[category]);
    });

    return {
      byCategory: resultByCategory,
      totals: Object.values(totalByLabor),
      hasData: Object.keys(resultByCategory).length > 0
    };
  }, [currentView, masterPlan, monthlyPlan, weeklyPlan, selectedTasks, manpowerConstants]);

  const getNodeIcon = (type: string) => {
    const icons: Record<string, typeof Building> = {
      building: Building,
      category: Wrench, // Category/Stage (RCC, Masonry, etc.)
      subcategory: FileText, // Subcategory (Concrete, Shuttering Work, etc.)
      floor: Layers,
      unit: Box // Unit (Flat 101, etc.)
    };
    return icons[type] || Layers;
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Planned': 'bg-gray-100 text-gray-800',
      'Started': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Get dependency task information
  const getDependencyInfo = (task) => {
    if (!task.dependencies || task.dependencies.length === 0) {
      return [];
    }
    const allTasks = getCurrentTasks();
    const dependencyTasks = task.dependencies
      .map(depId => allTasks.find(t => t.id === depId))
      .filter(Boolean);
    return dependencyTasks;
  };

  // Rolling window for "what counts as a live blocker". Tasks whose missed
  // finish date is older than this are considered stale and dropped from the
  // Blockers / Blocked Tasks filters and the trigger badges — otherwise
  // years-old stuck rows pollute the view and drown out recent, actionable
  // blockers. Same cutoff is applied across getBlockedByDependencies,
  // getDelayTriggerCount, and getTotalCascadeImpact so all three share one
  // definition of "live".
  const BLOCKER_LOOKBACK_DAYS = 90;

  // Check if a task is blocked by delayed dependencies
  const getBlockedByDependencies = (task) => {
    const dependencies = getDependencyInfo(task);
    if (dependencies.length === 0) return { isBlocked: false, blockedBy: [] };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const lookbackCutoff = new Date(today);
    lookbackCutoff.setDate(lookbackCutoff.getDate() - BLOCKER_LOOKBACK_DAYS);

    const blockedBy = dependencies.filter(dep => {
      if (dep.status === 'Completed') return false;

      // Get target finish date
      let targetFinish;
      if (dep.weeklyRevision?.finishDate) {
        targetFinish = new Date(dep.weeklyRevision.finishDate);
      } else if (dep.monthlyRevision?.finishDate) {
        targetFinish = new Date(dep.monthlyRevision.finishDate);
      } else {
        targetFinish = new Date(dep.originalSchedule?.finishDate || dep.finishDate);
      }
      targetFinish.setHours(0, 0, 0, 0);

      // Is this dependency delayed, and is the miss recent enough to still count?
      if (today <= targetFinish) return false;
      if (targetFinish < lookbackCutoff) return false;
      return true;
    });

    return { isBlocked: blockedBy.length > 0, blockedBy };
  };

  // Count how many tasks this task is blocking (Delay Trigger analysis)
  const getDelayTriggerCount = (task) => {
    // Only count if this task itself is delayed
    if (task.status === 'Completed') return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let targetFinish;
    if (task.weeklyRevision?.finishDate) {
      targetFinish = new Date(task.weeklyRevision.finishDate);
    } else if (task.monthlyRevision?.finishDate) {
      targetFinish = new Date(task.monthlyRevision.finishDate);
    } else {
      targetFinish = new Date(task.originalSchedule?.finishDate || task.finishDate);
    }
    targetFinish.setHours(0, 0, 0, 0);

    const isDelayed = today > targetFinish;
    if (!isDelayed) return 0;

    // Stale-blocker gate: only surface if the miss happened recently.
    const lookbackCutoff = new Date(today);
    lookbackCutoff.setDate(lookbackCutoff.getDate() - BLOCKER_LOOKBACK_DAYS);
    if (targetFinish < lookbackCutoff) return 0;

    // Find all tasks that depend on this one (immediate successors only)
    const allTasks = getCurrentTasks();
    const blockedTasks = allTasks.filter(t =>
      t.dependencies && t.dependencies.includes(task.id) && t.status !== 'Completed'
    );

    return blockedTasks.length;
  };

  // Calculate TOTAL cascade impact - all downstream tasks affected (recursive)
  const getTotalCascadeImpact = (task, visited = new Set()) => {
    // Only count if this task itself is delayed
    if (task.status === 'Completed') return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let targetFinish;
    if (task.weeklyRevision?.finishDate) {
      targetFinish = new Date(task.weeklyRevision.finishDate);
    } else if (task.monthlyRevision?.finishDate) {
      targetFinish = new Date(task.monthlyRevision.finishDate);
    } else {
      targetFinish = new Date(task.originalSchedule?.finishDate || task.finishDate);
    }
    targetFinish.setHours(0, 0, 0, 0);

    const isDelayed = today > targetFinish;
    if (!isDelayed) return 0;

    // Same stale-blocker gate as getDelayTriggerCount — keep the two in sync
    // so the "live blocker" definition is consistent across the filter,
    // badges, and cascade rollups.
    const lookbackCutoff = new Date(today);
    lookbackCutoff.setDate(lookbackCutoff.getDate() - BLOCKER_LOOKBACK_DAYS);
    if (targetFinish < lookbackCutoff) return 0;

    // Prevent infinite loops with circular dependencies
    if (visited.has(task.id)) return 0;
    visited.add(task.id);

    // Find all immediate successors
    const allTasks = getCurrentTasks();
    const immediateSuccessors = allTasks.filter(t =>
      t.dependencies && t.dependencies.includes(task.id) && t.status !== 'Completed'
    );

    // Count immediate successors + all their downstream tasks (recursively)
    let totalImpact = immediateSuccessors.length;

    immediateSuccessors.forEach(successor => {
      // Add downstream impact of each successor
      totalImpact += getTotalCascadeImpact(successor, new Set(visited));
    });

    return totalImpact;
  };

  // ============ SMART PLANNING ASSISTANT LOGIC ============
  // (Helper functions moved to planning/planningUtils.ts)

  // Milestone rollup chip shown on each tree node. Replaces the former
  // separate WBS/milestones panel — the same information is now inlined
  // into the existing Building → Category → Subcategory → Floor → Unit
  // tree. Tier precedence (major > minor > work) picks the chip's icon
  // and colour; the fully-achieved case overrides to a green trophy.
  const MilestoneBadge = ({
    stats,
  }: {
    stats?: import('./shared/types').MilestoneNodeStats;
  }) => {
    if (!stats || stats.total === 0) return null;
    const allAchieved = stats.achieved === stats.total;
    let Icon = Target;
    let cls = 'text-blue-600 bg-blue-50 border-blue-100 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700';
    if (allAchieved) {
      Icon = Trophy;
      cls = 'text-emerald-700 bg-emerald-50 border-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700';
    } else if (stats.hasMajor) {
      Icon = Flag;
      cls = 'text-indigo-700 bg-indigo-50 border-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-700';
    } else if (stats.hasMinor) {
      Icon = Folder;
      cls = 'text-amber-700 bg-amber-50 border-amber-100 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700';
    }
    const label = `${stats.achieved}/${stats.total} milestone${stats.total === 1 ? '' : 's'}`;
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded border ${cls}`}
        title={label + (stats.hasMajor ? ' (includes a major)' : stats.hasMinor ? ' (includes a minor)' : '')}
      >
        <Icon className="w-2.5 h-2.5" />
        {stats.achieved}/{stats.total}
      </span>
    );
  };

  // TreeNode component props interface
  interface TreeNodeProps {
    nodeKey: string;
    node: ApiTreeNode;
    level?: number;
    parentPath?: string[];
    parentFilters?: Partial<TaskFilters>;
  }

  const TreeNodeComponent = ({ nodeKey, node, level = 0, parentPath = [], parentFilters = {} }: TreeNodeProps) => {
    // Default: expand only building level (level 0), categories start collapsed
    const isExpanded = expandedNodes[nodeKey] ?? (level === 0);
    const Icon = getNodeIcon(node.type);
    const hasChildren = node.children && Array.isArray(node.children) && node.children.length > 0;
    const currentPath = [...parentPath, node.name];
    const isSelected = selectedNode === nodeKey;

    // Build filters for this node based on its type and hierarchy
    // Structure: Building → Category → Subcategory → Floor → Unit
    const buildFilters = (): TaskFilters => {
      const filters: TaskFilters = {
        project_id: selectedProjectId || '',
        ...parentFilters
      };

      switch (node.type) {
        case 'building':
          filters.building_id = node.id;
          break;
        case 'category':
          filters.category_id = node.id;
          break;
        case 'subcategory':
          filters.subcategory_id = node.id;
          break;
        case 'floor':
          filters.floor_id = node.id;
          break;
        case 'unit':
          filters.unit_id = node.id;
          break;
      }

      return filters;
    };

    const nodeFilters = buildFilters();

    return (
      <div className="select-none">
        <div
          className={`flex items-center space-x-2 py-2 px-3 hover:bg-gray-100 dark:hover:bg-neutral-800 cursor-pointer rounded-lg ${isSelected ? 'bg-blue-50 border-l-4 border-blue-500 dark:bg-neutral-800 dark:border-neutral-500' : ''}`}
          style={{ marginLeft: `${level * 20}px` }}
          onClick={() => selectNode(nodeKey, currentPath, nodeFilters)}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleNodeExpansion(nodeKey);
              }}
              className="p-1"
            >
              {isExpanded ?
                <ChevronDown className="w-4 h-4 text-gray-500" /> :
                <ChevronRight className="w-4 h-4 text-gray-500" />
              }
            </button>
          )}
          {!hasChildren && <div className="w-6" />}

          <Icon className="w-4 h-4 text-gray-600" />

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm text-gray-900 truncate">
                {node.name}
              </span>
              <div className="flex items-center gap-1.5 ml-2 whitespace-nowrap">
                <MilestoneBadge stats={node.milestoneStats} />
                <span className="text-xs text-gray-500">
                  {node.stats.total}
                </span>
              </div>
            </div>
          </div>
        </div>

        {isExpanded && hasChildren && (
          <div>
            {node.children!.map((childNode) => (
              <TreeNodeComponent
                key={childNode.id}
                nodeKey={`${nodeKey}.${childNode.id} `}
                node={childNode}
                level={level + 1}
                parentPath={currentPath}
                parentFilters={nodeFilters}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const TaskTable = () => {
    const filteredTasks = getFilteredTasksForTable();

    // Get unique engineers, contractors, floors, and series for filter dropdowns
    const allTasks = getCurrentTasks();
    const engineers = ['All', ...new Set(teamMembers.map(member => member.name).filter(Boolean))]; // Use teamMembers for engineers
    const contractors = ['All', ...new Set(allTasks.map(task => task.contractor).filter(Boolean))];
    const floors = ['All', ...new Set(allTasks.map(task => task.floor).filter(Boolean))];
    const seriesList = ['All', ...new Set(allTasks.map(task => task.series).filter(Boolean))];
    const unitList = ['All', ...new Set(allTasks.map(task => task.unit).filter(Boolean))];

    // Apply additional filters
    console.log('[Debug] allTasks count:', allTasks.length);
    const searchedTasks = filteredTasks.filter(task => {
      // Text search
      const matchesSearch = (task.taskName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.id || '').includes(searchTerm);

      // Stage filter
      const matchesStage = filterStage === 'All' || task.category === filterStage;

      // Engineer filter
      const matchesEngineer = filterEngineer === 'All' || task.assignedTo === filterEngineer;

      // Contractor filter  
      const matchesContractor = filterContractor === 'All' || task.contractor === filterContractor;

      // Floor filter
      const matchesFloor = filterFloor === 'All' || task.floor === filterFloor;

      // Series filter
      const matchesSeries = filterSeries === 'All' || task.series === filterSeries;

      // Unit filter
      const matchesUnit = filterUnit === 'All' || task.unit === filterUnit;

      // Status filter
      let matchesStatus = true;
      if (filterStatus === 'Exclude Completed') {
        matchesStatus = task.status !== 'Completed';
      } else if (filterStatus === 'Delayed') {
        // Task is delayed if current date is past the planned finish date AND task is not completed
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison

        // Use the most recent planned finish date (weekly > monthly > original)
        let plannedFinishDate;
        if (task.weeklyRevision?.finishDate) {
          plannedFinishDate = new Date(task.weeklyRevision.finishDate);
        } else if (task.monthlyRevision?.finishDate) {
          plannedFinishDate = new Date(task.monthlyRevision.finishDate);
        } else {
          plannedFinishDate = new Date(task.originalSchedule?.finishDate || task.finishDate);
        }
        plannedFinishDate.setHours(0, 0, 0, 0); // Reset time to start of day

        // Task is delayed if today is past the finish date and task is not completed
        const isOverdue = today > plannedFinishDate;
        matchesStatus = isOverdue && task.status !== 'Completed';
      } else if (filterStatus === 'Blocked') {
        // Show tasks that are blocked by delayed dependencies
        const blockInfo = getBlockedByDependencies(task);
        matchesStatus = blockInfo.isBlocked;
      } else if (filterStatus === 'Trigger') {
        // Show tasks that are causing delays (blocking other tasks)
        const triggerCount = getDelayTriggerCount(task);
        matchesStatus = triggerCount > 0;
      }

      // PHASE 1: Master Plan Overdue Filter
      const matchesOverdueFilter = !showOverdueFromMaster || (() => {
        const delayInfo = calculateMasterPlanDelay(task);
        return delayInfo.isOverdue;
      })();

      // PHASE 1: Ready to Start Filter (Work Fronts)
      const matchesReadyToStart = !showReadyToStart || isWorkFrontReady(task, allTasks);

      // Date range filter
      let matchesDateRange = true;
      if (filterDateStart || filterDateEnd) {
        const taskStartDate = new Date(task.originalSchedule?.startDate || task.startDate);
        const taskEndDate = new Date(task.originalSchedule?.finishDate || task.finishDate);

        if (filterDateStart) {
          const filterStart = new Date(filterDateStart);
          matchesDateRange = matchesDateRange && taskEndDate >= filterStart;
        }

        if (filterDateEnd) {
          const filterEnd = new Date(filterDateEnd);
          matchesDateRange = matchesDateRange && taskStartDate <= filterEnd;
        }
      }

      return matchesSearch && matchesStage && matchesStatus && matchesEngineer && matchesContractor && matchesFloor && matchesSeries && matchesUnit && matchesDateRange && matchesOverdueFilter && matchesReadyToStart;
    });

    // Virtualization Logic
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const [scrollTop, setScrollTop] = useState(0);
    const rowHeight = viewDensity === 'compact' ? 40 : 60; // Approximate row heights
    const overscan = 10; // Number of rows to render outside visible area

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      setScrollTop(e.currentTarget.scrollTop);
    };

    const virtualization = useMemo(() => {
      // If ref is not attached yet, return default
      // We need to use a default height if ref is null to avoid initial render issues
      const containerHeight = tableContainerRef.current?.clientHeight || 800;
      const totalRows = searchedTasks.length;

      const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
      const endIndex = Math.min(totalRows, Math.ceil((scrollTop + containerHeight) / rowHeight) + overscan);

      const startHeight = startIndex * rowHeight;
      const endHeight = Math.max(0, (totalRows - endIndex) * rowHeight);

      return { start: startIndex, end: endIndex, startHeight, endHeight };
    }, [scrollTop, searchedTasks.length, viewDensity]);

    // Reorder searched tasks so rows sharing a group key sit together and
    // collect which ids are the first row of their group. Purely visual —
    // the reorder is length-preserving so the virtualization math above is
    // unaffected. When the axis resolves to 'none' this is a no-op and the
    // table renders exactly as before.
    const groupAxis = computeAutoGroupAxis(selectedNodeFilters);
    const { tasks: orderedTasks, firstOfGroup } = useMemo(
      () => orderTasksByGroup(searchedTasks, groupAxis),
      [searchedTasks, groupAxis],
    );

    const visibleTasks = orderedTasks.slice(virtualization.start, virtualization.end);

    const toggleTaskSelection = (taskId) => {
      setSelectedTasks(prev =>
        prev.includes(taskId)
          ? prev.filter(id => id !== taskId)
          : [...prev, taskId]
      );
    };

    const selectAllTasks = () => {
      const allTaskIds = searchedTasks.map(task => task.id);
      setSelectedTasks(prev =>
        prev.length === allTaskIds.length && allTaskIds.every(id => prev.includes(id))
          ? []
          : allTaskIds
      );
    };

    const isAllSelected = searchedTasks.length > 0 &&
      searchedTasks.every(task => selectedTasks.includes(task.id));

    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-4 text-gray-600">Loading project...</span>
        </div>
      );
    }

    if (isLoadingTasks) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-4 text-gray-600">Loading tasks...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-full text-red-600">
          <AlertCircle className="w-6 h-6 mr-2" />
          <span>{error}</span>
        </div>
      );
    }

    // Show prompt to select a node when no tasks loaded and tree is available
    if (filteredTasks.length === 0 && filteredTree.length > 0 && !isLoadingTasks) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-500">
          <Building className="w-12 h-12 mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">Select a Node to View Tasks</h3>
          <p className="text-sm text-center max-w-md">
            Click on a building, category, floor, or unit in the tree structure on the left to load and view tasks.
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full">
        {/* Table Header with Breadcrumb and Actions */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex-shrink-0">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-slate-400 mb-3">
            <FileText className="w-4 h-4" />
            <span>{projects.find(p => p.id === selectedProjectId)?.name || 'Project'}</span>
            {breadcrumb.map((crumb, index) => (
              <React.Fragment key={index}>
                <ChevronRight className="w-3 h-3" />
                <span className={index === breadcrumb.length - 1 ? 'font-medium text-gray-900 dark:text-slate-100' : ''}>
                  {crumb}
                </span>
              </React.Fragment>
            ))}
          </div>

          {/* Search Row */}
          <div className="flex items-center space-x-4 mb-3">
            <div className="flex items-center space-x-2">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
              />
            </div>
            <div className="text-sm text-gray-500">
              {searchedTasks.length} tasks shown
            </div>
          </div>

          {/* Filters Panel */}
          <div className="mb-3 p-3 bg-gray-50 dark:bg-neutral-900 rounded-lg">
            {/* Quick Filters Row */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2 flex-wrap gap-2">
                {(() => {
                  // "This Week" = Monday → Sunday of the current ISO week, so
                  // it aligns with the weekly plans used elsewhere in the tool.
                  const toYmd = (d: Date) => {
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    return `${y}-${m}-${day}`;
                  };
                  const monday = mondayOf(new Date());
                  const sunday = new Date(monday);
                  sunday.setDate(monday.getDate() + 6);
                  const start = toYmd(monday);
                  const end = toYmd(sunday);
                  const active = filterDateStart === start && filterDateEnd === end;
                  return (
                    <button
                      onClick={() => {
                        if (active) {
                          setFilterDateStart('');
                          setFilterDateEnd('');
                        } else {
                          setFilterDateStart(start);
                          setFilterDateEnd(end);
                        }
                      }}
                      className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${active
                        ? 'bg-blue-600 text-white border border-blue-600'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:!bg-slate-900 dark:!text-slate-200 dark:!border-slate-600 dark:hover:!bg-slate-800'
                      }`}
                    >
                      This Week
                    </button>
                  );
                })()}
                <button
                  onClick={() => setFilterStatus(filterStatus === 'Exclude Completed' ? 'All' : 'Exclude Completed')}
                  className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${filterStatus === 'Exclude Completed'
                    ? 'bg-green-600 text-white border border-green-600'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:!bg-slate-900 dark:!text-slate-200 dark:!border-slate-600 dark:hover:!bg-slate-800'
                  }`}
                >
                  Exclude Completed
                </button>
                <button
                  onClick={() => setFilterStatus(filterStatus === 'Delayed' ? 'All' : 'Delayed')}
                  className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${filterStatus === 'Delayed'
                    ? 'bg-red-600 text-white border border-red-600'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:!bg-slate-900 dark:!text-slate-200 dark:!border-slate-600 dark:hover:!bg-slate-800'
                  }`}
                >
                  Delayed Tasks
                </button>
                <button
                  onClick={() => setFilterStatus(filterStatus === 'Blocked' ? 'All' : 'Blocked')}
                  className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${filterStatus === 'Blocked'
                    ? 'bg-amber-600 text-white border border-amber-600'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:!bg-slate-900 dark:!text-slate-200 dark:!border-slate-600 dark:hover:!bg-slate-800'
                  }`}
                  title="Tasks waiting on a delayed dependency — only dependencies missed within the last 3 months count"
                >
                  <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Blocked Tasks</span>
                </button>
                <button
                  onClick={() => setFilterStatus(filterStatus === 'Trigger' ? 'All' : 'Trigger')}
                  className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${filterStatus === 'Trigger'
                    ? 'bg-orange-600 text-white border border-orange-600'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:!bg-slate-900 dark:!text-slate-200 dark:!border-slate-600 dark:hover:!bg-slate-800'
                  }`}
                  title="Tasks holding up successors — limited to blockers whose miss is within the last 3 months"
                >
                  <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Blockers</span>
                </button>

                {/* PHASE 1: Smart Planning Assistant Filters */}
                <button
                  onClick={() => setShowOverdueFromMaster(!showOverdueFromMaster)}
                  className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${showOverdueFromMaster
                    ? 'bg-red-700 text-white border border-red-700'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:!bg-slate-900 dark:!text-slate-200 dark:!border-slate-600 dark:hover:!bg-slate-800'
                  }`}
                  title="Tasks past their original master plan date"
                >
                  <span className="inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Overdue from Master Plan</span>
                </button>
                <button
                  onClick={() => setShowReadyToStart(!showReadyToStart)}
                  className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${showReadyToStart
                    ? 'bg-green-600 text-white border border-green-600'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:!bg-slate-900 dark:!text-slate-200 dark:!border-slate-600 dark:hover:!bg-slate-800'
                  }`}
                  title="Work that can be started (all dependencies complete)"
                >
                  <span className="inline-flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Open Workfronts</span>
                </button>

                {getActiveFiltersCount() > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="h-8 px-3 rounded-md text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                  >
                    Clear All ({getActiveFiltersCount()})
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="h-8 flex items-center space-x-1 px-3 rounded-md text-xs font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:!bg-slate-900 dark:!text-slate-200 dark:!border-slate-600 dark:hover:!bg-slate-800 transition-colors"
              >
                <Settings className="w-3.5 h-3.5" />
                <span>Advanced</span>
                {showAdvancedFilters ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            </div>

            {/* Advanced Filter Controls - Expands within same panel */}
            {showAdvancedFilters && (
              <div className="flex space-x-3 mt-3 pt-3 border-t border-blue-200 flex-wrap gap-3">

                {/* Stage Filter */}
                <div className="flex flex-col space-y-1">
                  <label className="text-xs font-medium text-gray-700">Stage:</label>
                  <select
                    value={filterStage}
                    onChange={(e) => setFilterStage(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="All">All Stages</option>
                    {Object.keys(CONSTRUCTION_STAGES).map(stage => (
                      <option key={stage} value={stage}>{stage}</option>
                    ))}
                  </select>
                </div>

                {/* Engineer Filter */}
                <div className="flex flex-col space-y-1">
                  <label className="text-xs font-medium text-gray-700">Engineer:</label>
                  <select
                    value={filterEngineer}
                    onChange={(e) => setFilterEngineer(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {engineers.map(engineer => (
                      <option key={engineer} value={engineer}>
                        {engineer === 'All' ? 'All Engineers' : engineer}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Contractor Filter */}
                <div className="flex flex-col space-y-1">
                  <label className="text-xs font-medium text-gray-700">Contractor:</label>
                  <select
                    value={filterContractor}
                    onChange={(e) => setFilterContractor(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {contractors.map(contractor => (
                      <option key={contractor} value={contractor}>
                        {contractor === 'All' ? 'All Contractors' : contractor}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Floor Filter */}
                <div className="flex flex-col space-y-1">
                  <label className="text-xs font-medium text-gray-700">Floor:</label>
                  <select
                    value={filterFloor}
                    onChange={(e) => setFilterFloor(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {floors.map(floor => (
                      <option key={floor} value={floor}>
                        {floor === 'All' ? 'All Floors' : floor === 'N/A' ? 'Site-wide' : floor}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Series Filter */}
                <div className="flex flex-col space-y-1">
                  <label className="text-xs font-medium text-gray-700">Series:</label>
                  <select
                    value={filterSeries}
                    onChange={(e) => setFilterSeries(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {seriesList.map(series => (
                      <option key={series} value={series}>
                        {series === 'All' ? 'All Series' : series === 'Common Area' ? 'Common Area' : `Series ${series} `}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Unit Filter */}
                <div className="flex flex-col space-y-1">
                  <label className="text-xs font-medium text-gray-700">Unit:</label>
                  <select
                    value={filterUnit}
                    onChange={(e) => setFilterUnit(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {unitList.map(unit => (
                      <option key={unit} value={unit}>
                        {unit === 'All' ? 'All Units' : unit}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date Range */}
                <div className="flex flex-col space-y-1">
                  <label className="text-xs font-medium text-gray-700">From:</label>
                  <input
                    type="date"
                    value={filterDateStart}
                    onChange={(e) => setFilterDateStart(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex flex-col space-y-1">
                  <label className="text-xs font-medium text-gray-700">To:</label>
                  <input
                    type="date"
                    value={filterDateEnd}
                    onChange={(e) => setFilterDateEnd(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Clear Filters Button */}
                {(filterEngineer !== 'All' || filterContractor !== 'All' || filterFloor !== 'All' || filterSeries !== 'All' || filterUnit !== 'All' || filterDateStart || filterDateEnd || searchTerm) && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilterEngineer('All');
                      setFilterContractor('All');
                      setFilterFloor('All');
                      setFilterSeries('All');
                      setFilterDateStart('');
                      setFilterDateEnd('');
                    }}
                    className="px-2 py-1 text-xs text-red-600 hover:text-red-800 border border-red-300 rounded hover:bg-red-50"
                  >
                    Clear All
                  </button>
                )}
              </div>
            )}
          </div>

          {/* View Mode Toggle and Column Selector */}
          <div className="flex items-center justify-between mb-4 px-4 py-2 bg-gray-50 dark:bg-slate-800/50 border-y border-gray-200 dark:border-slate-700 flex-shrink-0">
            <div className="flex items-center space-x-2 flex-shrink-0">
              <span className="text-xs font-medium text-gray-700 dark:text-slate-300">View:</span>
              <div className="inline-flex rounded-lg bg-gray-100 dark:bg-slate-800 p-0.5" role="group">
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`h-8 inline-flex items-center px-3 text-xs font-medium rounded-md transition-all ${viewMode === 'table'
                    ? 'bg-white text-blue-600 shadow-sm dark:!bg-slate-800 dark:text-indigo-300'
                    : 'text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-100'
                  }`}
                >
                  <Table className="w-4 h-4 mr-1.5" />
                  Table
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('gantt')}
                  className={`h-8 inline-flex items-center px-3 text-xs font-medium rounded-md transition-all ${viewMode === 'gantt'
                    ? 'bg-white text-blue-600 shadow-sm dark:!bg-slate-800 dark:text-indigo-300'
                    : 'text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-100'
                  }`}
                >
                  <BarChart3 className="w-4 h-4 mr-1.5" />
                  Gantt
                </button>
                {/* Dependency view hidden - replaced by Critical Path in Gantt
                <button
                  type="button"
                  onClick={() => setViewMode('dependency')}
                  className={`h-8 inline-flex items-center px-3 text-xs font-medium rounded-md transition-all ${viewMode === 'dependency'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Network className="w-4 h-4 mr-1.5" />
                  Dependencies
                </button>
                */}
              </div>
            </div>

            {viewMode === 'table' && (
              <div className="flex items-center space-x-2 flex-shrink-0">
                {/* View Density Toggle - Segmented Control Style */}
                <div className="inline-flex rounded-lg bg-gray-100 dark:bg-slate-800 p-0.5" role="group">
                  <button
                    type="button"
                    onClick={() => setViewDensity('normal')}
                    className={`h-8 inline-flex items-center px-3 text-xs font-medium rounded-md transition-all ${viewDensity === 'normal'
                      ? 'bg-white text-blue-600 shadow-sm dark:!bg-slate-800 dark:text-indigo-300'
                      : 'text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-100'
                    }`}
                  >
                    Normal
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewDensity('compact')}
                    className={`h-8 inline-flex items-center px-3 text-xs font-medium rounded-md transition-all ${viewDensity === 'compact'
                      ? 'bg-white text-blue-600 shadow-sm dark:!bg-slate-800 dark:text-indigo-300'
                      : 'text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-100'
                    }`}
                  >
                    Compact
                  </button>
                </div>

                {/* Column Selector */}
                <div className="relative">
                  <button
                    onClick={() => setShowColumnSelector(!showColumnSelector)}
                    className="h-8 flex items-center space-x-1.5 px-3 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:!bg-slate-900 dark:!text-slate-200 dark:!border-slate-600 dark:hover:!bg-slate-800"
                  >
                    <Settings className="w-3.5 h-3.5" />
                    <span>Columns</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>

                  {showColumnSelector && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowColumnSelector(false)}
                      />
                      <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg z-20 py-2">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase border-b border-gray-200 dark:border-slate-700">
                          Show/Hide Columns
                        </div>
                        {[
                          { key: 'floor', label: 'Floor' },
                          { key: 'locationUnit', label: 'Unit (location)' },
                          { key: 'task', label: 'Task' },
                          { key: 'stage', label: 'Stage' },
                          { key: 'series', label: 'Series' },
                          { key: 'unit', label: 'UOM (measurement)' },
                          { key: 'status', label: 'Status' },
                          { key: 'progress', label: 'Progress' },
                          { key: 'cost', label: 'Cost' },
                          { key: 'schedule', label: 'Original Schedule' },
                          { key: 'target', label: 'Target Date' },
                          { key: 'delay', label: 'Delay Status' },
                          { key: 'dependencies', label: 'Dependency Impact (Blocked/Trigger)' },
                          { key: 'revision', label: 'Revision History (Hidden by default)' },
                          { key: 'assigned', label: 'Assigned To' }
                        ].map(col => (
                          <label
                            key={col.key}
                            className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={visibleColumns[col.key]}
                              onChange={(e) => setVisibleColumns(prev => ({
                                ...prev,
                                [col.key]: e.target.checked
                              }))}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                            />
                            <span className="text-sm text-gray-700">{col.label}</span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Smart Planning Assistants */}
          {currentView === 'monthly' && selectedTasks.length === 0 && (
            <div className="mb-4 px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-neutral-900 dark:to-neutral-800 border border-blue-200 dark:border-neutral-700 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-neutral-100 mb-1 inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Smart Planning Assistants</h3>
                  <p className="text-xs text-gray-600 dark:text-neutral-400">Get intelligent recommendations for your monthly plan</p>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      setShowSmartPlanningModal(true);
                      setPlanningStep(1);
                      setSelectedActivities([]);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 text-sm font-medium shadow-md"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>Smart Plan Next Month</span>
                  </button>
                  <button
                    onClick={() => setShowResourceTimelineModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg hover:from-green-700 hover:to-teal-700 text-sm font-medium shadow-md"
                  >
                    <Calendar className="w-4 h-4" />
                    <span>Resource Timeline</span>
                  </button>
                  <button
                    onClick={() => setShowResourcePlannerModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 text-sm font-medium shadow-md"
                  >
                    <Users className="w-4 h-4" />
                    <span>Resource Planner</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {selectedTasks.length > 0 && (
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-sm text-gray-600">
                {selectedTasks.length} selected
              </span>

              {/* View Resources Button - Always visible when tasks are selected */}
              <button
                onClick={() => {
                  setShowMaterialsModal(true);
                  setResourceTab('materials');
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
              >
                <FileText className="w-4 h-4" />
                <span>View Resources ({selectedTasks.length} tasks)</span>
              </button>

              {/* Add to Monthly Plan — next 6 months with auto-create on click. */}
              {currentView === 'master' && (
                <div className="relative">
                  <button
                    onClick={() => setShowAddToPlanDropdown(!showAddToPlanDropdown)}
                    disabled={isAddingToPlan}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isAddingToPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    <span>Add to Monthly Plan</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>

                  {showAddToPlanDropdown && (() => {
                    const virtuals = nextNMonths(6);
                    const forwardKeys = new Set(virtuals.map(v => `${v.year}-${v.month}`));
                    const realByKey = new Map(monthlyPlans.map(p => [`${p.year}-${p.month}`, p]));
                    const historicalDrafts = monthlyPlans
                      .filter(p => p.status === 'draft' && !forwardKeys.has(`${p.year}-${p.month}`))
                      .sort((a, b) => b.year !== a.year ? b.year - a.year : b.month - a.month);

                    // Shared handler: add selected tasks to a (possibly
                    // virtual) month. If virtual, create the monthly plan
                    // first, then POST the tasks. Published/closed real plans
                    // are silently skipped — the button is rendered disabled.
                    const addToMonth = async (vm: VirtualMonth, existing: MonthlyPlan | undefined) => {
                      if (!selectedProjectId || selectedTasks.length === 0) return;
                      try {
                        setIsAddingToPlan(true);
                        setMaterializingKey(vm.key);

                        let target = existing;
                        if (!target) {
                          target = await api.createMonthlyPlan(selectedProjectId, {
                            year: vm.year,
                            month: vm.month,
                            name: vm.name,
                          });
                        } else if (target.status !== 'draft') {
                          alert(`${vm.name} is ${target.status}; tasks can only be added to draft plans.`);
                          return;
                        }

                        const taskIds = selectedTasks.map(id => parseInt(id));
                        const result = await api.addTasksToMonthlyPlan(target.id, taskIds);

                        const updatedPlans = await api.getMonthlyPlans(selectedProjectId);
                        setMonthlyPlans(updatedPlans);
                        setSelectedTasks([]);
                        setShowAddToPlanDropdown(false);

                        if (result.added > 0) {
                          const verb = existing ? 'Added' : 'Created plan and added';
                          // Non-blocking confirmation. Keep it short so the
                          // planner can keep bulk-assigning without dismissing
                          // modals between each batch.
                          console.log(`${verb} ${result.added} task(s) to ${vm.name}`);
                        }
                      } catch (err: any) {
                        alert(err?.message || 'Failed to add tasks to plan');
                      } finally {
                        setIsAddingToPlan(false);
                        setMaterializingKey(null);
                      }
                    };

                    return (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowAddToPlanDropdown(false)} />
                        <div className="absolute left-0 top-full mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase border-b border-gray-100">
                            Next 6 months
                          </div>
                          <div className="max-h-64 overflow-y-auto">
                            {virtuals.map(vm => {
                              const real = realByKey.get(`${vm.year}-${vm.month}`);
                              const isBusy = materializingKey === vm.key;
                              const disabled = isBusy || (real && real.status !== 'draft');
                              return (
                                <button
                                  key={vm.key}
                                  disabled={!!disabled}
                                  onClick={() => addToMonth(vm, real)}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={real && real.status !== 'draft' ? `${vm.name} is ${real.status}` : undefined}
                                >
                                  <span className="flex items-center gap-2">
                                    <Calendar className={`w-4 h-4 ${real ? 'text-blue-500' : 'text-gray-300'}`} />
                                    <span className={real ? '' : 'text-gray-600'}>{vm.name}</span>
                                  </span>
                                  {isBusy ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />
                                  ) : real ? (
                                    <span className="text-xs text-gray-400">
                                      {real.total_tasks || 0} tasks
                                    </span>
                                  ) : (
                                    <span className="px-1.5 py-0.5 text-[10px] rounded border border-dashed border-gray-300 text-gray-400 uppercase tracking-wide">
                                      new
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          {historicalDrafts.length > 0 && (
                            <>
                              <div className="px-3 pt-2 pb-1 text-xs font-semibold text-gray-500 uppercase border-t border-gray-100 mt-1">
                                Other drafts
                              </div>
                              <div className="max-h-40 overflow-y-auto">
                                {historicalDrafts.map(plan => (
                                  <button
                                    key={plan.id}
                                    onClick={async () => {
                                      try {
                                        setIsAddingToPlan(true);
                                        const taskIds = selectedTasks.map(id => parseInt(id));
                                        await api.addTasksToMonthlyPlan(plan.id, taskIds);
                                        setSelectedTasks([]);
                                        setShowAddToPlanDropdown(false);
                                        const updatedPlans = await api.getMonthlyPlans(selectedProjectId!);
                                        setMonthlyPlans(updatedPlans);
                                      } catch (err: any) {
                                        alert(err?.message || 'Failed to add tasks to plan');
                                      } finally {
                                        setIsAddingToPlan(false);
                                      }
                                    }}
                                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between"
                                  >
                                    <span className="flex items-center gap-2">
                                      <Calendar className="w-4 h-4 text-blue-500" />
                                      {MONTHS[plan.month - 1]} {plan.year}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                      {plan.total_tasks || 0} tasks
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Move to Weekly — every week of the selected month with
                  auto-create on click. Tasks can only move to weeks in the
                  same monthly plan since the API needs `planTaskId` (the
                  `monthly_plan_tasks` junction row), which is scoped to one
                  monthly plan. */}
              {currentView === 'monthly' && selectedMonthlyPlan && (
                <div className="relative">
                  <button
                    onClick={() => setShowMoveToWeekDropdown(!showMoveToWeekDropdown)}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <ArrowRight className="w-4 h-4" />
                    <span>Move to Weekly</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>

                  {showMoveToWeekDropdown && (() => {
                    const virtuals = weeksForMonth(selectedMonthlyPlan.year, selectedMonthlyPlan.month);
                    const realByStart = new Map(weeklyPlans.map(w => [w.start_date, w]));

                    const moveToWeek = async (vw: VirtualWeek, existing: WeeklyPlan | undefined) => {
                      if (!selectedMonthlyPlan) return;
                      const selectedMonthlyTasks = monthlyPlanTasks.filter(t => selectedTasks.includes(t.id));
                      const planTaskIds = selectedMonthlyTasks
                        .map(t => (t as any).planTaskId)
                        .filter(Boolean);
                      if (planTaskIds.length === 0) {
                        alert('No valid tasks selected');
                        return;
                      }
                      try {
                        setMaterializingKey(vw.key);
                        let target = existing;
                        if (!target) {
                          // Auto-create the week inside the current monthly plan.
                          // Resolve a non-conflicting week_number so we don't
                          // collide with legacy sequentially-numbered rows.
                          const weekNumber = nextAvailableWeekNumber(
                            weeklyPlans.map(w => w.week_number),
                            vw.weekNumber,
                          );
                          target = await api.createWeeklyPlan({
                            monthly_plan_id: selectedMonthlyPlan.id,
                            week_number: weekNumber,
                            start_date: vw.startDate,
                            end_date: vw.endDate,
                            name: `Week ${weekNumber}`,
                          });
                        } else if (target.status !== 'draft') {
                          alert(`Week ${target.week_number} is ${target.status}; tasks can only be added to draft weeks.`);
                          return;
                        }

                        const result = await api.addTasksToWeeklyPlan(target.id, planTaskIds);
                        if (result.added > 0) {
                          const verb = existing ? 'Added' : 'Created week and added';
                          alert(`${verb} ${result.added} task(s) to Week ${target.week_number}`);
                        }
                        setSelectedTasks([]);
                        setShowMoveToWeekDropdown(false);
                        const refreshed = await api.getWeeklyPlans(selectedMonthlyPlan.id);
                        setWeeklyPlans(refreshed);
                        await refreshProjectWeeklyPlans();
                      } catch (err: any) {
                        console.error('Failed to move tasks:', err);
                        alert(err?.message || 'Failed to move tasks');
                      } finally {
                        setMaterializingKey(null);
                      }
                    };

                    return (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowMoveToWeekDropdown(false)} />
                        <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase border-b border-gray-100">
                            Weeks · {MONTHS[selectedMonthlyPlan.month - 1]} {selectedMonthlyPlan.year}
                          </div>
                          <div className="max-h-80 overflow-y-auto">
                            {virtuals.map(vw => {
                              const real = realByStart.get(vw.startDate);
                              const isBusy = materializingKey === vw.key;
                              const disabled = isBusy || (real && real.status !== 'draft');
                              const dateLabel = `${new Date(vw.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(vw.endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                              return (
                                <button
                                  key={vw.key}
                                  disabled={!!disabled}
                                  onClick={() => moveToWeek(vw, real)}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={real && real.status !== 'draft' ? `Week is ${real.status}` : undefined}
                                >
                                  <span className="flex flex-col">
                                    <span className="flex items-center gap-2">
                                      <CalendarDays className={`w-4 h-4 ${real ? 'text-green-500' : 'text-gray-300'}`} />
                                      <span className={real ? '' : 'text-gray-600'}>
                                        {real ? `Week ${real.week_number}` : vw.name}
                                      </span>
                                    </span>
                                    <span className="text-xs text-gray-500 ml-6">{dateLabel}</span>
                                  </span>
                                  {isBusy ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-green-600" />
                                  ) : real ? (
                                    <span className={`px-1.5 py-0.5 text-xs rounded ${
                                      real.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                                      real.status === 'published' ? 'bg-green-100 text-green-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {real.status}
                                    </span>
                                  ) : (
                                    <span className="px-1.5 py-0.5 text-[10px] rounded border border-dashed border-gray-300 text-gray-400 uppercase tracking-wide">
                                      new
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Raise Concern button */}
              <button 
                onClick={() => {
                  setConcernPreSelectedTasks(selectedTasks.map(id => typeof id === 'string' ? parseInt(id) : id));
                  setShowRaiseConcernModal(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm"
              >
                <AlertTriangle className="w-4 h-4" />
                <span>Raise Concern</span>
              </button>

              {/* Remove from Plan button */}
              {currentView !== 'master' && (
                <button 
                  onClick={() => {
                    // TODO: Implement remove from plan
                    setSelectedTasks([]);
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                >
                  <X className="w-4 h-4" />
                  <span>Remove from Plan</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Active Filters Display */}
        {(filterStatus !== 'All' || filterEngineer !== 'All' || filterContractor !== 'All' || filterFloor !== 'All' || filterSeries !== 'All' || filterUnit !== 'All' || filterDateStart || filterDateEnd) && (
          <div className="px-4 py-2 bg-blue-50 border-t border-blue-200">
            <div className="flex items-center space-x-4 text-sm">
              <span className="font-medium text-blue-900">Active Filters:</span>
              {filterStatus !== 'All' && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  Status: {filterStatus}
                </span>
              )}
              {filterEngineer !== 'All' && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  Engineer: {filterEngineer}
                </span>
              )}
              {filterContractor !== 'All' && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  Contractor: {filterContractor}
                </span>
              )}
              {filterFloor !== 'All' && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  Floor: {filterFloor === 'N/A' ? 'Site-wide' : filterFloor}
                </span>
              )}
              {filterSeries !== 'All' && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  Series: {filterSeries === 'Common Area' ? 'Common Area' : `Series ${filterSeries} `}
                </span>
              )}
              {filterUnit !== 'All' && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  Unit: {filterUnit}
                </span>
              )}
              {(filterDateStart || filterDateEnd) && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  Date: {filterDateStart || 'start'} to {filterDateEnd || 'end'}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Table or Gantt Chart View */}
        <div
          className="flex-1 overflow-auto"
          ref={tableContainerRef}
          onScroll={handleScroll}
        >
          {viewMode === 'table' ? (
            <table className="border-collapse" style={{ width: 'max-content', minWidth: '100%' }}>
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="w-12 pl-4 pr-0 py-2 text-left align-top">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={selectAllTasks}
                      className="rounded mt-1"
                    />
                  </th>
                  {visibleColumns.floor && (
                    <th className="min-w-[80px] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Floor
                    </th>
                  )}
                  {visibleColumns.locationUnit && (
                    <th className="min-w-[80px] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unit
                    </th>
                  )}
                  {visibleColumns.task && (
                    <th className="min-w-[200px] pl-2 pr-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Task
                    </th>
                  )}
                  {visibleColumns.stage && (
                    <th className="min-w-[80px] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stage
                    </th>
                  )}
                  {visibleColumns.series && (
                    <th className="min-w-[80px] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Series
                    </th>
                  )}
                  {visibleColumns.status && (
                    <th className="min-w-[80px] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  )}
                  {visibleColumns.unit && (
                    <th className="min-w-[70px] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      UOM
                    </th>
                  )}
                  {visibleColumns.progress && (
                    <th className="min-w-[90px] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progress
                    </th>
                  )}
                  {visibleColumns.cost && (
                    <th className="min-w-[90px] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cost
                    </th>
                  )}
                  {visibleColumns.schedule && (
                    <th className="min-w-[120px] px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Original Schedule
                    </th>
                  )}
                  {visibleColumns.target && (
                    <th className="min-w-[120px] px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Target Date
                    </th>
                  )}
                  {visibleColumns.delay && (
                    <th className="min-w-[100px] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Delay Status
                    </th>
                  )}
                  {visibleColumns.dependencies && (
                    <th className="min-w-[140px] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dependency Impact
                    </th>
                  )}
                  {visibleColumns.revision && (
                    <>
                      <th className="min-w-[120px] px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Monthly Revision
                      </th>
                      <th className="min-w-[120px] px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Weekly Revision
                      </th>
                      <th className="min-w-[90px] px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Variance
                      </th>
                    </>
                  )}
                  {visibleColumns.assigned && (
                    <th className="min-w-[180px] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assignment
                    </th>
                  )}
                  <th className="min-w-[70px] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr style={{ height: `${virtualization.startHeight}px` }}>
                  <td colSpan={100} />
                </tr>
                {visibleTasks.map(task => {
                  const completionRate = (task.quantity && task.quantity > 0) ? (((task.quantityCompleted || 0) / task.quantity) * 100).toFixed(1) : '0.0';

                  // Group divider: thick top border on the first row of each
                  // group, except the very first row of the whole ordered
                  // list (nothing above it to separate from). The absolute
                  // check against orderedTasks[0] keeps the divider correct
                  // even when virtualization clips the top of the table.
                  const isGroupStart =
                    firstOfGroup.has(task.id) && task.id !== orderedTasks[0]?.id;

                  // Calculate variances with fallbacks
                  const originalFinishDate = task.originalSchedule?.finishDate || task.finishDate;

                  const monthlyVariance = task.monthlyRevision && task.monthlyRevision.finishDate
                    ? calculateVariance(originalFinishDate, task.monthlyRevision.finishDate)
                    : { days: 0, status: 'on-track' };

                  const weeklyVariance = task.weeklyRevision && task.weeklyRevision.finishDate
                    ? calculateVariance(
                      task.monthlyRevision?.finishDate || originalFinishDate,
                      task.weeklyRevision.finishDate
                    )
                    : { days: 0, status: 'on-track' };

                  const totalVariance = task.weeklyRevision && task.weeklyRevision.finishDate
                    ? calculateVariance(originalFinishDate, task.weeklyRevision.finishDate)
                    : monthlyVariance;

                  const stageInfo = CONSTRUCTION_STAGES[task.category] || { order: 0, color: 'bg-gray-100 text-gray-800' };

                  return (
                    <tr
                      key={task.id}
                      className={`group hover:bg-gray-50 ${viewDensity === 'compact' ? 'text-xs' : ''} ${
                        isGroupStart ? '!border-t-2 !border-gray-400 dark:!border-slate-500' : ''
                      }`}
                    >
                      {/* Checkbox */}
                      <td className={`pl-4 pr-0 align-middle ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                        <input
                          type="checkbox"
                          checked={selectedTasks.includes(task.id)}
                          onChange={() => toggleTaskSelection(task.id)}
                          className={`rounded ${viewDensity === 'compact' ? 'mt-0.5' : 'mt-1'}`}
                        />
                      </td>

                      {/* Floor — leads the row so the table reads as a
                          floor-by-floor worklist. */}
                      {visibleColumns.floor && (
                        <td className={`px-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-neutral-200 rounded text-xs font-medium">
                            {task.floor || 'N/A'}
                          </span>
                        </td>
                      )}

                      {/* Location Unit (e.g. A101). Separate from the
                          measurement UOM column further right. */}
                      {visibleColumns.locationUnit && (
                        <td className={`px-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-neutral-200 rounded text-xs font-medium">
                            {task.unit || '-'}
                          </span>
                        </td>
                      )}

                      {/* Task */}
                      {visibleColumns.task && (
                        <td className={`pl-2 pr-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                          <div className="flex items-center space-x-1">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {task.taskName}
                            </div>
                            {(() => {
                              const deps = getDependencyInfo(task);
                              if (deps.length > 0) {
                                return (
                                  <div className="group relative flex-shrink-0">
                                    <GitBranch className="w-3.5 h-3.5 text-orange-500" />
                                    <div className="hover-card-dark absolute left-0 bottom-full mb-1 hidden group-hover:block z-10 w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg border border-white/30">
                                      <div className="font-semibold mb-1">Depends on:</div>
                                      {deps.map((dep, idx) => (
                                        <div key={dep.id} className="truncate">
                                          {idx + 1}. {dep.taskName}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            {/* Plan Assignment Badges */}
                            {currentView === 'master' && task.activeMonthlyPlanId && (
                              <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  task.activePlanStatus === 'published' 
                                    ? 'bg-green-100 text-green-700' 
                                    : task.activePlanStatus === 'closed'
                                    ? 'bg-gray-100 text-gray-600'
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  <Calendar className="w-2.5 h-2.5" />
                                  {MONTHS[task.activePlanMonth! - 1]?.slice(0, 3)} {task.activePlanYear?.toString().slice(-2)}
                                </span>
                                {task.activeWeeklyPlanId && (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                                    <CalendarDays className="w-2.5 h-2.5" />
                                    W{task.activeWeekNumber}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          {viewDensity === 'normal' && (
                            <div className="text-xs text-gray-500 truncate mt-0.5">
                              ID: {task.id} • {task.subCategory}
                            </div>
                          )}
                        </td>
                      )}

                      {/* Stage */}
                      {visibleColumns.stage && (
                        <td className={`px-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${stageInfo.color}`}>
                            {task.category}
                          </span>
                        </td>
                      )}

                      {/* Series */}
                      {visibleColumns.series && (
                        <td className={`px-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-neutral-200 rounded text-xs font-medium">
                            {task.series || 'N/A'}
                          </span>
                        </td>
                      )}

                      {/* Status */}
                      {visibleColumns.status && (
                        <td className={`px-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(task.status)}`}>
                            {task.status}
                          </span>
                        </td>
                      )}

                      {/* Task Unit (measurement) */}
                      {visibleColumns.unit && (
                        <td className={`px-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                          <span className="text-sm text-gray-700">
                            {task.taskUnit || '-'}
                          </span>
                        </td>
                      )}

                      {/* Progress */}
                      {visibleColumns.progress && (
                        <td className={`px-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                          <div className="text-sm text-gray-900">
                            {task.quantityCompleted || 0} / {task.quantity || 0}
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                            <div
                              className="bg-blue-600 h-1.5 rounded-full"
                              style={{ width: `${Math.min(parseFloat(completionRate), 100)}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{completionRate}%</div>
                        </td>
                      )}

                      {/* Cost */}
                      {visibleColumns.cost && (
                        <td className={`px-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                          <div className="text-sm text-gray-900">₹{((task.estimatedAmount || 0) / 100000).toFixed(1)}L</div>
                          <div className="text-xs text-gray-500">₹{((task.actualCost || 0) / 100000).toFixed(1)}L</div>
                        </td>
                      )}

                      {/* Original Schedule */}
                      {visibleColumns.schedule && (
                        <td className={`px-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                          <div className="text-sm text-gray-900">
                            {formatDate(task.originalSchedule?.startDate || task.startDate)}
                          </div>
                          <div className="text-sm text-gray-600">
                            {formatDate(task.originalSchedule?.finishDate || task.finishDate)}
                          </div>
                        </td>
                      )}

                      {/* Target Date - Most recent planned finish */}
                      {visibleColumns.target && (
                        <td className={`px-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                          {(() => {
                            // Get the most recent planned finish date
                            let targetDate, source;
                            if (task.weeklyRevision?.finishDate) {
                              targetDate = task.weeklyRevision.finishDate;
                              source = 'Weekly';
                            } else if (task.monthlyRevision?.finishDate) {
                              targetDate = task.monthlyRevision.finishDate;
                              source = 'Monthly';
                            } else {
                              targetDate = task.originalSchedule?.finishDate || task.finishDate;
                              source = 'Original';
                            }

                            return (
                              <>
                                <div className="text-sm text-blue-700 font-medium">
                                  {formatDate(targetDate)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {source}
                                </div>
                              </>
                            );
                          })()}
                        </td>
                      )}

                      {/* Delay Status - Days overdue/ahead vs target */}
                      {visibleColumns.delay && (
                        <td className={`px-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                          {(() => {
                            if (task.status === 'Completed') {
                              return <div className="text-sm text-gray-500 italic">Done</div>;
                            }

                            const today = new Date();
                            today.setHours(0, 0, 0, 0);

                            // Use most recent planned finish date
                            let plannedFinish;
                            if (task.weeklyRevision?.finishDate) {
                              plannedFinish = new Date(task.weeklyRevision.finishDate);
                            } else if (task.monthlyRevision?.finishDate) {
                              plannedFinish = new Date(task.monthlyRevision.finishDate);
                            } else {
                              plannedFinish = new Date(task.originalSchedule?.finishDate || task.finishDate);
                            }
                            plannedFinish.setHours(0, 0, 0, 0);

                            const diffTime = today - plannedFinish;
                            const daysOverdue = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                            // Validate that daysOverdue is a valid number
                            if (isNaN(daysOverdue)) {
                              return <div className="text-sm text-gray-400 italic">N/A</div>;
                            }

                            let statusColor, statusLabel;
                            if (daysOverdue > 7) {
                              statusColor = 'text-red-700 bg-red-50';
                              statusLabel = `${daysOverdue}d overdue`;
                            } else if (daysOverdue > 0) {
                              statusColor = 'text-orange-700 bg-orange-50';
                              statusLabel = `${daysOverdue}d overdue`;
                            } else if (daysOverdue > -7) {
                              statusColor = 'text-yellow-700 bg-yellow-50';
                              statusLabel = `${Math.abs(daysOverdue)}d left`;
                            } else {
                              statusColor = 'text-green-700 bg-green-50';
                              statusLabel = `${Math.abs(daysOverdue)}d ahead`;
                            }

                            return (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${statusColor}`}>
                                {statusLabel}
                              </span>
                            );
                          })()}
                        </td>
                      )}

                      {/* Dependency Impact - Blocked by or Triggering delays */}
                      {visibleColumns.dependencies && (
                        <td className={`px-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                          {(() => {
                            const blockInfo = getBlockedByDependencies(task);
                            const triggerCount = getDelayTriggerCount(task);
                            const totalImpact = getTotalCascadeImpact(task);

                            if (!blockInfo.isBlocked && triggerCount === 0) {
                              return <div className="text-sm text-gray-400 italic">-</div>;
                            }

                            return (
                              <div className="flex items-center gap-2">
                                {blockInfo.isBlocked && <span className="inline-flex items-center gap-0.5 text-sm text-red-600"><Lock className="w-3.5 h-3.5" /> {blockInfo.blockedBy.length}</span>}
                                {totalImpact > 0 && <span className="inline-flex items-center gap-0.5 text-sm text-red-700 font-bold"><AlertTriangle className="w-3.5 h-3.5" /> {totalImpact}</span>}
                              </div>
                            );
                          })()}
                        </td>
                      )}

                      {/* Monthly Revision */}
                      {visibleColumns.revision && (
                        <>
                          <td className={`px-2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'}`}>
                            {editingTask === task.id && (currentView === 'monthly' || currentView === 'weekly') ? (
                              <div className="space-y-1">
                                <input
                                  type="date"
                                  value={editFormData.monthlyStartDate}
                                  onChange={(e) => updateEditFormData('monthlyStartDate', e.target.value)}
                                  className="w-full border border-blue-300 rounded px-1 py-0.5 text-xs"
                                  placeholder="Start date"
                                />
                                <input
                                  type="date"
                                  value={editFormData.monthlyFinishDate}
                                  onChange={(e) => updateEditFormData('monthlyFinishDate', e.target.value)}
                                  className="w-full border border-blue-300 rounded px-1 py-0.5 text-xs"
                                  placeholder="End date"
                                />
                                <select
                                  value={editFormData.monthlyReason}
                                  onChange={(e) => updateEditFormData('monthlyReason', e.target.value)}
                                  className="w-full border border-blue-300 rounded px-1 py-0.5 text-xs"
                                >
                                  <option value="">Select reason</option>
                                  <option value="Weather delays">Weather delays</option>
                                  <option value="Material shortage">Material shortage</option>
                                  <option value="Equipment issues">Equipment issues</option>
                                  <option value="Resource unavailability">Resource unavailability</option>
                                  <option value="Site conditions">Site conditions</option>
                                  <option value="Client changes">Client changes</option>
                                </select>
                              </div>
                            ) : task.monthlyRevision ? (
                              <>
                                <div className="text-blue-900 font-medium inline-flex items-center gap-1">
                                  <FileText className="w-3 h-3 flex-shrink-0" />
                                  {task.monthlyRevision.startDate}
                                </div>
                                <div className="text-blue-700">
                                  {task.monthlyRevision.finishDate}
                                </div>
                                <div className="text-xs text-blue-500">
                                  {monthlyVariance.days > 0 ? `+ ${monthlyVariance.days} d` :
                                    monthlyVariance.days < 0 ? `${monthlyVariance.days} d` : 'On time'}
                                </div>
                              </>
                            ) : (currentView === 'monthly' || currentView === 'weekly') ? (
                              <button
                                onClick={() => startEditing(task)}
                                className="text-blue-600 hover:text-blue-800 italic text-xs"
                              >
                                + Set monthly plan
                              </button>
                            ) : (
                              <div className="text-gray-400 italic">Not set</div>
                            )}
                          </td>

                          {/* Weekly Revision */}
                          <td className={`px - 2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'} `}>
                            {editingTask === task.id && currentView === 'weekly' ? (
                              <div className="space-y-1">
                                <input
                                  type="date"
                                  value={editFormData.weeklyStartDate}
                                  onChange={(e) => updateEditFormData('weeklyStartDate', e.target.value)}
                                  className="w-full border border-green-300 rounded px-1 py-0.5 text-xs"
                                  placeholder="Start date"
                                />
                                <input
                                  type="date"
                                  value={editFormData.weeklyFinishDate}
                                  onChange={(e) => updateEditFormData('weeklyFinishDate', e.target.value)}
                                  className="w-full border border-green-300 rounded px-1 py-0.5 text-xs"
                                  placeholder="End date"
                                />
                                <input
                                  type="text"
                                  value={editFormData.weeklyNotes}
                                  onChange={(e) => updateEditFormData('weeklyNotes', e.target.value)}
                                  className="w-full border border-green-300 rounded px-1 py-0.5 text-xs"
                                  placeholder="Notes"
                                />
                              </div>
                            ) : task.weeklyRevision ? (
                              <>
                                <div className="text-green-900 font-medium inline-flex items-center gap-1">
                                  <ClipboardList className="w-3 h-3 flex-shrink-0" />
                                  {task.weeklyRevision.startDate}
                                </div>
                                <div className="text-green-700">
                                  {task.weeklyRevision.finishDate}
                                </div>
                                <div className="text-xs text-green-500">
                                  {weeklyVariance.days > 0 ? `+ ${weeklyVariance.days} d` :
                                    weeklyVariance.days < 0 ? `${weeklyVariance.days} d` : 'On time'}
                                </div>
                              </>
                            ) : currentView === 'weekly' ? (
                              <button
                                onClick={() => startEditing(task)}
                                className="text-green-600 hover:text-green-800 italic text-xs"
                              >
                                + Set weekly plan
                              </button>
                            ) : (
                              <div className="text-gray-400 italic">Not set</div>
                            )}
                          </td>

                          {/* Total Variance */}
                          <td className={`px - 2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'} `}>
                            <span className={`px - 2 py - 1 rounded text - sm font - medium ${getVarianceColor(totalVariance.status)} `}>
                              {totalVariance.days > 0 ? `+ ${totalVariance.days} d` : `${totalVariance.days} d`}
                            </span>
                          </td>
                        </>
                      )}

                      {/* Assignment */}
                      {visibleColumns.assigned && (
                        <td className={`px - 2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'} `}>
                          {editingTask === task.id ? (
                            <div className="space-y-1">
                              <select
                                value={editFormData.assignedTo}
                                onChange={(e) => updateEditFormData('assignedTo', e.target.value)}
                                className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                              >
                                <option value="">Select...</option>
                                {teamMembers
                                  .filter(member => {
                                    // Strict filtering: If task is in a building, only show members assigned to that building.
                                    // This filters out general managers and cross-tower staff, targeting leaf-level Site Engineers.
                                    if (task.buildingId) {
                                      return member.buildingId === task.buildingId;
                                    }
                                    return true; // For general tasks, show everyone
                                  })
                                  .map(member => (
                                    <option key={member.id} value={member.name}>
                                      {member.name} ({member.role}){member.buildingName ? ` [${member.buildingName}]` : ''}
                                    </option>
                                  ))}
                              </select>
                              <select
                                value={editFormData.contractor}
                                onChange={(e) => updateEditFormData('contractor', e.target.value)}
                                className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                              >
                                {contractors.filter(cont => cont !== 'All').map(contractor => (
                                  <option key={contractor} value={contractor}>{contractor}</option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <div className="flex flex-col items-start gap-1 min-w-0">
                              {task.assignedTo ? (
                                <span
                                  className={`inline-flex items-center gap-2 rounded-full font-semibold bg-amber-100 text-amber-950 dark:bg-amber-950/70 dark:text-amber-100 ${
                                    viewDensity === 'compact' ? 'px-2 py-0.5 text-sm' : 'px-2.5 py-1.5 text-base'
                                  }`}
                                  title={task.assignedTo}
                                >
                                  <span className={`rounded-full bg-amber-600 text-white font-bold flex items-center justify-center flex-shrink-0 ${
                                    viewDensity === 'compact' ? 'w-5 h-5 text-[10px]' : 'w-7 h-7 text-xs'
                                  }`}>
                                    {personInitials(task.assignedTo)}
                                  </span>
                                  <span className="truncate max-w-[12rem] leading-tight">{task.assignedTo}</span>
                                </span>
                              ) : (
                                <span className="text-sm text-gray-400 dark:text-neutral-500">Unassigned</span>
                              )}
                              {viewDensity !== 'compact' && task.contractor && (
                                <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-neutral-400 truncate max-w-[12rem]">
                                  <Building className="w-3 h-3 flex-shrink-0" />
                                  {task.contractor}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      )}

                      {/* Actions */}
                      <td className={`px - 2 ${viewDensity === 'compact' ? 'py-1' : 'py-2'} `}>
                        {editingTask === task.id ? (
                          <div className="flex space-x-1">
                            <button
                              onClick={saveEditing}
                              className="p-1 hover:bg-green-100 rounded text-green-600"
                              title="Save"
                            >
                              <Save className="w-3 h-3" />
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="p-1 hover:bg-red-100 rounded text-red-600"
                              title="Cancel"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (currentView === 'monthly' || currentView === 'weekly') ? (
                          <div className="flex space-x-1">
                            <button
                              onClick={() => startEditing(task)}
                              className="p-1 hover:bg-gray-200 rounded"
                              title="Edit"
                            >
                              <Edit3 className="w-3 h-3 text-gray-600" />
                            </button>
                            <button
                              onClick={() => removeFromPlan(task.id)}
                              className="p-1 hover:bg-red-100 rounded text-red-600"
                              title="Remove from Plan"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            className="p-1 bg-gray-100 rounded cursor-not-allowed"
                            title="Editing disabled in Master view"
                            disabled
                          >
                            <Edit3 className="w-3 h-3 text-gray-400" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ height: `${virtualization.endHeight} px` }}>
                  <td colSpan={100} />
                </tr>
              </tbody>
            </table>
          ) : viewMode === 'gantt' ? (
            /* Gantt Chart View */
            <GanttChartView tasks={searchedTasks} getDependencyInfo={getDependencyInfo} />
          ) : (
            /* Dependency Tree View */
            <DependencyTreeView
              tasks={searchedTasks}
              getDependencyInfo={getDependencyInfo}
              getBlockedByDependencies={getBlockedByDependencies}
              getDelayTriggerCount={getDelayTriggerCount}
              getTotalCascadeImpact={getTotalCascadeImpact}
            />
          )}

          {searchedTasks.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks Found</h3>
              <p className="text-gray-600">
                {searchTerm ? 'Try adjusting your search term.' : 'No tasks match the current filters.'}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen w-full bg-gray-50 dark:bg-slate-900 flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 px-6 py-4 flex-shrink-0">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {projects.find(p => p.id === selectedProjectId)?.name || 'Construction Plan'}
              </h1>
              <select
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              >
                {projects.length === 0 && <option disabled value="">No projects found</option>}
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              {/* WBS Sync sits right next to the project name so "sync this
                  project" reads as one action. Compact variant: icon +
                  "Sync <project>" + tiny timestamp. Reloads the page on
                  success so the tree and task counts pick up fresh data. */}
              <WbsSyncControl
                projectName={projects.find(p => p.id === selectedProjectId)?.name}
                onSyncComplete={() => window.location.reload()}
              />
            </div>
            <p className="text-gray-600">Construction Planning Management</p>
          </div>

          <div className="flex items-center space-x-4">
            {/* Plan Tabs with Dropdowns */}
            <div className="flex items-center bg-gray-100 dark:bg-slate-800 rounded-lg p-1">
              {/* Master Plan Tab - Always visible */}
              <button
                onClick={() => setCurrentView('master')}
                className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${currentView === 'master'
                  ? 'bg-white text-blue-600 shadow-sm dark:!bg-slate-800 dark:text-indigo-300'
                  : 'text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-100'
                  }`}
              >
                <span className="flex items-center gap-1.5">
                  <ListTodo className="w-3.5 h-3.5" />
                  Master Plan
                </span>
              </button>

              {/* Monthly Plans Dropdown */}
              <div className="relative">
              <button
                  onClick={() => {
                    setShowMonthlyDropdown(!showMonthlyDropdown);
                    setShowWeeklyDropdown(false);
                  }}
                  className={`px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${currentView === 'monthly'
                  ? 'bg-white text-blue-600 shadow-sm dark:!bg-slate-800 dark:text-indigo-300'
                  : 'text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-100'
                  }`}
              >
                  <Calendar className="w-3.5 h-3.5" />
                  {selectedMonthlyPlan 
                    ? `${MONTHS[selectedMonthlyPlan.month - 1].slice(0, 3)} ${selectedMonthlyPlan.year}`
                    : 'Monthly Plans'
                  }
                  <ChevronDown className="w-3 h-3" />
              </button>

                {showMonthlyDropdown && (() => {
                  // Build the merged "next 6 months" list: for each virtual
                  // month, surface the real plan if one exists, otherwise the
                  // virtual placeholder. Then below a divider, show any older
                  // real plans that aren't in the forward window (so historical
                  // drafts and recent closed months stay reachable).
                  const virtuals = nextNMonths(6);
                  const forwardKeys = new Set(virtuals.map(v => `${v.year}-${v.month}`));
                  const realByKey = new Map(monthlyPlans.map(p => [`${p.year}-${p.month}`, p]));
                  const historical = monthlyPlans
                    .filter(p => !forwardKeys.has(`${p.year}-${p.month}`))
                    .filter(p => {
                      const now = new Date();
                      const currentYear = now.getFullYear();
                      const currentMonth = now.getMonth() + 1;
                      return p.status !== 'closed' ||
                        (p.year === currentYear && p.month >= currentMonth - 1) ||
                        (p.year === currentYear - 1 && p.month === 12 && currentMonth === 1);
                    })
                    .sort((a, b) => b.year !== a.year ? b.year - a.year : b.month - a.month);

                  return (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowMonthlyDropdown(false)} />
                      <div className="absolute left-0 top-full mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase border-b border-gray-100">
                          Next 6 months
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          {virtuals.map(vm => {
                            const real = realByKey.get(`${vm.year}-${vm.month}`);
                            const isSelected = real && selectedMonthlyPlan?.id === real.id;
                            const isBusy = materializingKey === vm.key;
                            return (
                              <button
                                key={vm.key}
                                disabled={isBusy}
                                onClick={async () => {
                                  if (real) {
                                    setSelectedMonthlyPlan(real);
                                    setCurrentView('monthly');
                                    setShowMonthlyDropdown(false);
                                  } else {
                                    await materializeMonth(vm);
                                    setShowMonthlyDropdown(false);
                                  }
                                }}
                                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between disabled:opacity-60 ${
                                  isSelected ? 'bg-blue-50 text-blue-700' : ''
                                }`}
                              >
                                <span className="flex items-center gap-2">
                                  <Calendar className={`w-4 h-4 ${real ? 'text-gray-400' : 'text-gray-300'}`} />
                                  <span className={real ? '' : 'text-gray-600'}>{vm.name}</span>
                                </span>
                                {isBusy ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />
                                ) : real ? (
                                  <span className={`px-1.5 py-0.5 text-xs rounded ${
                                    real.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                                    real.status === 'published' ? 'bg-green-100 text-green-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {real.status}
                                  </span>
                                ) : (
                                  <span className="px-1.5 py-0.5 text-[10px] rounded border border-dashed border-gray-300 text-gray-400 uppercase tracking-wide">
                                    new
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {historical.length > 0 && (
                          <>
                            <div className="px-3 pt-2 pb-1 text-xs font-semibold text-gray-500 uppercase border-t border-gray-100 mt-1">
                              Other plans
                            </div>
                            <div className="max-h-40 overflow-y-auto">
                              {historical.map(plan => (
                                <button
                                  key={plan.id}
                                  onClick={() => {
                                    setSelectedMonthlyPlan(plan);
                                    setCurrentView('monthly');
                                    setShowMonthlyDropdown(false);
                                  }}
                                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                    selectedMonthlyPlan?.id === plan.id ? 'bg-blue-50 text-blue-700' : ''
                                  }`}
                                >
                                  <span className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-gray-400" />
                                    {MONTHS[plan.month - 1]} {plan.year}
                                  </span>
                                  <span className={`px-1.5 py-0.5 text-xs rounded ${
                                    plan.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                                    plan.status === 'published' ? 'bg-green-100 text-green-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {plan.status}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                        <div className="border-t border-gray-100 mt-1 pt-1">
                          <button
                            onClick={() => {
                              setShowMonthlyPlansModal(true);
                              setShowMonthlyDropdown(false);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                          >
                            <Plus className="w-4 h-4" />
                            Custom plan…
                          </button>
                          <button
                            onClick={() => {
                              setShowMonthlyPlansModal(true);
                              setShowMonthlyDropdown(false);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <FolderKanban className="w-4 h-4" />
                            Manage All Plans
                          </button>
                        </div>
                      </div>
                    </>
                  );
                })()}
            </div>

              {/* Weekly Plans Dropdown — always enabled. Shows the next 6
                  Monday-anchored weeks merged with whatever real weekly plans
                  already exist for those dates across every monthly plan. */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowWeeklyDropdown(!showWeeklyDropdown);
                    setShowMonthlyDropdown(false);
                  }}
                  className={`px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${currentView === 'weekly'
                    ? 'bg-white text-blue-600 shadow-sm dark:!bg-slate-800 dark:text-indigo-300'
                    : 'text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-100'
                    }`}
                >
                  <CalendarDays className="w-3.5 h-3.5" />
                  {selectedWeeklyPlan
                    ? `Week ${selectedWeeklyPlan.week_number}`
                    : 'Weekly Plans'
                  }
                  <ChevronDown className="w-3 h-3" />
                </button>

                {showWeeklyDropdown && (() => {
                  const virtuals = nextNWeeks(6);
                  const realByStart = new Map(projectWeeklyPlans.map(w => [w.start_date, w]));
                  const forwardStarts = new Set(virtuals.map(v => v.startDate));
                  // Any real weeks for the selected month that fall outside
                  // the 6-week forward window — keep them reachable below a
                  // divider so historical weekly work isn't orphaned.
                  const historicalForSelectedMonth = selectedMonthlyPlan
                    ? weeklyPlans
                        .filter(w => !forwardStarts.has(w.start_date))
                        .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
                    : [];

                  return (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowWeeklyDropdown(false)} />
                      <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase border-b border-gray-100">
                          Next 6 weeks
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                          {virtuals.map(vw => {
                            const real = realByStart.get(vw.startDate);
                            const isSelected = real && selectedWeeklyPlan?.id === real.id;
                            const isBusy = materializingKey === vw.key;
                            const dateLabel = `${new Date(vw.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(vw.endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                            return (
                              <button
                                key={vw.key}
                                disabled={isBusy}
                                onClick={async () => {
                                  if (real) {
                                    // Also select the owning month so the
                                    // existing per-month state stays coherent.
                                    const owningMonth = monthlyPlans.find(p => p.id === real.monthly_plan_id) || null;
                                    if (owningMonth) setSelectedMonthlyPlan(owningMonth);
                                    setSelectedWeeklyPlan(real);
                                    setCurrentView('weekly');
                                    setShowWeeklyDropdown(false);
                                  } else {
                                    await materializeWeek(vw);
                                    setShowWeeklyDropdown(false);
                                  }
                                }}
                                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between disabled:opacity-60 ${
                                  isSelected ? 'bg-blue-50 text-blue-700' : ''
                                }`}
                              >
                                <span className="flex flex-col">
                                  <span className="flex items-center gap-2">
                                    <CalendarDays className={`w-4 h-4 ${real ? 'text-gray-400' : 'text-gray-300'}`} />
                                    <span className={real ? '' : 'text-gray-600'}>
                                      {real ? `Week ${real.week_number}` : vw.name}
                                      <span className="text-xs text-gray-400 ml-1.5">
                                        · {MONTHS[vw.month - 1].slice(0, 3)} {vw.year}
                                      </span>
                                    </span>
                                  </span>
                                  <span className="text-xs text-gray-500 ml-6">{dateLabel}</span>
                                </span>
                                {isBusy ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />
                                ) : real ? (
                                  <span className="flex items-center gap-1.5">
                                    <span className={`px-1.5 py-0.5 text-xs rounded ${
                                      real.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                                      real.status === 'published' ? 'bg-green-100 text-green-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {real.status}
                                    </span>
                                    {real.status === 'draft' && (
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        title={`Delete Week ${real.week_number}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteWeeklyPlan(real);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            deleteWeeklyPlan(real);
                                          }
                                        }}
                                        className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="px-1.5 py-0.5 text-[10px] rounded border border-dashed border-gray-300 text-gray-400 uppercase tracking-wide">
                                    new
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {historicalForSelectedMonth.length > 0 && selectedMonthlyPlan && (
                          <>
                            <div className="px-3 pt-2 pb-1 text-xs font-semibold text-gray-500 uppercase border-t border-gray-100 mt-1">
                              Other weeks · {MONTHS[selectedMonthlyPlan.month - 1]} {selectedMonthlyPlan.year}
                            </div>
                            <div className="max-h-40 overflow-y-auto">
                              {historicalForSelectedMonth.map(week => (
                                <button
                                  key={week.id}
                                  onClick={() => {
                                    setSelectedWeeklyPlan(week);
                                    setCurrentView('weekly');
                                    setShowWeeklyDropdown(false);
                                  }}
                                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                    selectedWeeklyPlan?.id === week.id ? 'bg-blue-50 text-blue-700' : ''
                                  }`}
                                >
                                  <span className="flex flex-col">
                                    <span className="flex items-center gap-2">
                                      <CalendarDays className="w-4 h-4 text-gray-400" />
                                      Week {week.week_number}
                                    </span>
                                    <span className="text-xs text-gray-500 ml-6">
                                      {new Date(week.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(week.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </span>
                                  </span>
                                  <span className="flex items-center gap-1.5">
                                    <span className={`px-1.5 py-0.5 text-xs rounded ${
                                      week.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                                      week.status === 'published' ? 'bg-green-100 text-green-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {week.status}
                                    </span>
                                    {week.status === 'draft' && (
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        title={`Delete Week ${week.week_number}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteWeeklyPlan(week);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            deleteWeeklyPlan(week);
                                          }
                                        }}
                                        className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </span>
                                    )}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                        <div className="border-t border-gray-100 mt-1 pt-1">
                          <button
                            onClick={() => {
                              // Custom-date week creation still uses the
                              // existing modal. Pre-fill from the selected
                              // month if there is one, else from today.
                              const anchor = selectedMonthlyPlan
                                ? new Date(selectedMonthlyPlan.year, selectedMonthlyPlan.month - 1, 1)
                                : new Date();
                              const existingWeeks = selectedMonthlyPlan ? weeklyPlans : [];
                              const nextWeekNum = existingWeeks.length + 1;
                              let startDate = anchor;
                              if (existingWeeks.length > 0) {
                                const lastWeek = existingWeeks[existingWeeks.length - 1];
                                startDate = new Date(lastWeek.end_date);
                                startDate.setDate(startDate.getDate() + 1);
                              }
                              const endDate = new Date(startDate);
                              endDate.setDate(endDate.getDate() + 6);
                              setNewWeeklyPlan({
                                week_number: nextWeekNum,
                                start_date: startDate.toISOString().split('T')[0],
                                end_date: endDate.toISOString().split('T')[0],
                                name: `Week ${nextWeekNum}`,
                              });
                              setShowWeeklyDropdown(false);
                              setShowCreateWeeklyModal(true);
                            }}
                            disabled={!selectedMonthlyPlan}
                            className="w-full px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2 disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                            title={selectedMonthlyPlan ? 'Custom week dates' : 'Pick a month first to create a custom week'}
                          >
                            <Plus className="w-4 h-4" />
                            Custom week…
                          </button>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Add concern — lives next to the Master / Monthly / Weekly
                plan tabs since raising a concern is a planning-time action
                scoped to the currently open plan. Styled as a pill so it
                visually belongs to the same action row. */}
            <button
              onClick={() => setShowConcernsModal(true)}
              className="flex items-center space-x-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 dark:bg-amber-900/50 dark:text-amber-200 dark:hover:bg-amber-800/60 dark:border-amber-700 transition-colors"
              title="Raise or review concerns for this project"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Add concern</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Tree Navigation */}
        <div
          className="bg-white border-r border-gray-200 flex flex-col flex-shrink-0"
          style={{ width: leftPanelWidth }}
        >
          {/* Mode toggle: browse tasks by location (default) or by milestone */}
          <div className="p-2 border-b border-gray-200 bg-white">
            <div className="inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50 w-full">
              <button
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition ${leftPanelMode === 'location' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                onClick={() => setLeftPanelMode('location')}
              >
                By location
              </button>
              <button
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition ${leftPanelMode === 'milestones' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                onClick={() => setLeftPanelMode('milestones')}
              >
                By milestone
              </button>
            </div>
          </div>

          {leftPanelMode === 'location' && (
            <>
              {/* Building Dropdown */}
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <label className="text-xs font-medium text-gray-700 block mb-2">Building Filter:</label>
                <select
                  value={selectedBuilding}
                  onChange={(e) => {
                    setSelectedBuilding(e.target.value);
                    setSelectedNode('root');
                    setBreadcrumb(['All Buildings']);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="All">All Buildings</option>
                  {/* Source building options from the tree (which has every building
                      for the project) rather than `masterPlan` — the master plan only
                      holds tasks for the currently-selected tree node. */}
                  {projectTree
                    .filter((b) => b.stats?.total > 0)
                    .map((b) => (
                      <option key={b.id} value={b.name}>{b.name}</option>
                    ))}
                </select>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {/* Root node - loads all tasks for the project */}
                <div
                  className={`flex items - center space - x - 2 py - 2 px - 3 hover: bg - gray - 100 cursor - pointer rounded - lg mb - 2 ${selectedNode === 'root' ? 'bg-blue-50 border-l-4 border-blue-500' : ''} `}
                  onClick={() => selectNode('root', ['All Buildings'], { project_id: selectedProjectId || '' })}
                >
                  <FileText className="w-4 h-4 text-gray-600" />
                  <span className="font-medium text-sm text-gray-900">
                    {selectedBuilding === 'All' ? 'All Buildings' : selectedBuilding}
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    ({getCurrentTasks().filter(t => selectedBuilding === 'All' || t.building === selectedBuilding).length})
                  </span>
                </div>

                {filteredTree.map((node) => (
                  <TreeNodeComponent
                    key={node.id}
                    nodeKey={node.id}
                    node={node}
                    level={0}
                    parentPath={['All Buildings']}
                    parentFilters={{}}
                  />
                ))}
              </div>
            </>
          )}

          {leftPanelMode === 'milestones' && selectedProjectId && (() => {
            // Pass the currently-picked building through as scope so the
            // milestone pool tracks the planner's current focus.
            const buildingNode = selectedBuilding !== 'All'
              ? projectTree.find((b) => b.name === selectedBuilding)
              : null;
            return (
              <MilestonesBrowsePanel
                projectId={selectedProjectId}
                buildingId={buildingNode?.id || null}
                scopeLabel={buildingNode ? `Building: ${buildingNode.name}` : null}
                onSelectionChange={handleMilestoneSelectionChange}
              />
            );
          })()}

          {leftPanelMode === 'milestones' && !selectedProjectId && (
            <div className="p-6 text-center text-sm text-gray-500">
              Select a project to browse milestones.
            </div>
          )}
        </div>

        {/* Resizable Separator */}
        <div
          className="w-1 bg-gray-200 dark:bg-slate-700/30 hover:bg-blue-400 dark:hover:bg-blue-500 cursor-col-resize flex-shrink-0 transition-colors group relative"
          onMouseDown={handleMouseDown}
        >
          <div className="absolute inset-y-0 -left-1 -right-1 group-hover:bg-blue-400/20" />
        </div>

        {/* Right Panel - Filtered Task Table */}
        <div className="flex-1 min-w-0 bg-white flex flex-col">
          {loadedMilestoneSummary && loadedMilestoneSummary.milestoneIds.length > 0 && (
            <div className="px-4 py-2 border-b border-blue-100 bg-blue-50 flex items-center gap-2">
              <Flag className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="text-xs text-blue-900 flex-1 min-w-0 truncate">
                <span className="font-semibold">{loadedMilestoneSummary.milestoneIds.length}</span> milestone{loadedMilestoneSummary.milestoneIds.length === 1 ? '' : 's'} loaded:
                <span className="ml-1 text-blue-700">
                  {loadedMilestoneSummary.names.slice(0, 3).join(', ')}
                  {loadedMilestoneSummary.names.length > 3 ? `, +${loadedMilestoneSummary.names.length - 3} more` : ''}
                </span>
              </span>
              <button
                className="text-xs text-blue-700 hover:text-blue-900 hover:underline"
                onClick={() => {
                  setLoadedMilestoneSummary(null);
                  setMasterPlan([]);
                  setSelectedTasks([]);
                  setSelectedNode('root');
                  setBreadcrumb(['All Buildings']);
                }}
              >
                Clear
              </button>
            </div>
          )}
          <TaskTable />
        </div>

        {/* Assignment Dock - Only show in Weekly view when tasks are selected */}
        {currentView === 'weekly' && selectedTasks.length > 0 && (
          <div className="w-80 bg-white dark:bg-neutral-900 border-l border-gray-200 dark:border-neutral-800 flex flex-col flex-shrink-0 overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-950">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-bold text-gray-900 dark:text-neutral-100 tracking-wide">ASSIGNMENT DOCK</h3>
                <span className="text-xs text-gray-500 dark:text-neutral-400 flex items-center">
                  <Clock className="w-3 h-3 mr-1" />
                  Auto-saved
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-neutral-400">
                Select tasks and click an engineer to assign work.
              </p>
            </div>

            {/* Search */}
            <div className="p-3 border-b border-gray-200 dark:border-neutral-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-neutral-500" />
                <input
                  type="text"
                  placeholder="Find engineer..."
                  value={assignmentSearchTerm}
                  onChange={(e) => setAssignmentSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder-neutral-500 rounded-lg focus:ring-2 focus:ring-neutral-500 focus:border-neutral-500"
                />
              </div>
            </div>

            {/* Engineer List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {filteredEngineers.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-neutral-400 text-sm">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No engineers found</p>
                  <p className="text-xs">Add team members in Settings</p>
                </div>
              ) : (
                filteredEngineers.map((engineer) => {
                  const { taskCount, load } = getEngineerStats(engineer.name);
                  const availability = getEngineerAvailability(engineer.name);
                  const isSelected = selectedTasks.length > 0;

                  return (
                    <div
                      key={engineer.id}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-dashed border-neutral-400 dark:border-neutral-500 bg-neutral-100 dark:bg-neutral-800 hover:border-neutral-300 cursor-pointer'
                          : 'border-gray-100 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-900'
                      }`}
                      onClick={() => isSelected && assignTasksToEngineer(engineer.name)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          {/* Avatar */}
                          <div className="relative">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-neutral-500 to-neutral-700 flex items-center justify-center text-white font-bold text-sm">
                              {engineer.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </div>
                            {availability === 'AVAILABLE' && (
                              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-neutral-800 flex items-center justify-center">
                                <Check className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-neutral-100 text-sm">{engineer.name}</h4>
                            <p className="text-xs text-gray-500 dark:text-neutral-400">{engineer.role || 'Engineer'}</p>
                          </div>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${
                          availability === 'AVAILABLE' 
                            ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400'
                            : availability === 'BUSY'
                            ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400'
                            : 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400'
                        }`}>
                          {availability}
                        </span>
                      </div>

                      <div className="flex items-center space-x-6 text-xs">
                        <div>
                          <span className="text-gray-500 dark:text-neutral-400 block">LOAD</span>
                          <span className={`font-bold ${
                            load >= 90 ? 'text-red-600 dark:text-red-400' : load >= 70 ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-900 dark:text-neutral-100'
                          }`}>
                            {load}%
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-neutral-400 block">TASKS</span>
                          <span className="font-bold text-gray-900 dark:text-neutral-100">{taskCount}</span>
                        </div>
                      </div>

                      {isSelected && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            assignTasksToEngineer(engineer.name);
                          }}
                          className="mt-3 w-full py-2.5 bg-neutral-800 hover:bg-neutral-700 dark:bg-neutral-200 dark:hover:bg-white dark:text-neutral-900 text-white rounded-lg text-sm font-medium flex items-center justify-center transition-colors"
                        >
                          <Plus className="w-4 h-4 mr-1.5" />
                          Assign {selectedTasks.length} Selected
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer Stats */}
            <div className="p-3 border-t border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-950">
              <div className="flex items-center justify-center text-xs text-gray-500 dark:text-neutral-400">
                <span>{selectedTasks.length} task{selectedTasks.length !== 1 ? 's' : ''} selected</span>
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Materials Request Modal */}
      {showMaterialsModal && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setShowMaterialsModal(false)}
          ></div>

          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden pointer-events-auto border border-gray-200">
              {/* Modal Header */}
              <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Resource Requirement</h2>
                      <p className="text-gray-600 text-sm">
                        {selectedTasks.length} tasks selected • {currentView === 'monthly' ? 'Monthly' : currentView === 'weekly' ? 'Weekly' : 'Master'} Plan
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowMaterialsModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex space-x-2 border-b border-gray-200">
                  <button
                    onClick={() => setResourceTab('materials')}
                    className={`px - 4 py - 2 text - sm font - medium transition - colors border - b - 2 ${resourceTab === 'materials'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                      } `}
                  >
                    Materials
                  </button>
                  <button
                    onClick={() => setResourceTab('labour')}
                    className={`px - 4 py - 2 text - sm font - medium transition - colors border - b - 2 ${resourceTab === 'labour'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                      } `}
                  >
                    Labour
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {resourceTab === 'materials' ? (
                  (() => {
                    const aggregatedMaterials = getAggregatedMaterials();
                    const categories = Object.keys(aggregatedMaterials);
                    const grandTotal = Object.values(aggregatedMaterials)
                      .flat()
                      .reduce((sum, material) => sum + material.total, 0);

                    if (categories.length === 0) {
                      return (
                        <div className="text-center py-12">
                          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Materials Found</h3>
                          <p className="text-gray-600">
                            The selected tasks don't have material information available.
                          </p>
                        </div>
                      );
                    }

                    return (
                      <>
                        {/* Project Info */}
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Project:</span>
                              <span className="ml-2 font-medium text-gray-900">{projects.find(p => p.id === selectedProjectId)?.name || 'Project'} {breadcrumb.length > 1 ? `- ${breadcrumb[breadcrumb.length - 1]}` : ''}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Date:</span>
                              <span className="ml-2 font-medium text-gray-900">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Building:</span>
                              <span className="ml-2 font-medium text-gray-900">{selectedBuilding}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Tasks:</span>
                              <span className="ml-2 font-medium text-gray-900">{selectedTasks.length} tasks</span>
                            </div>
                          </div>
                        </div>

                        {/* Materials by Category */}
                        {categories.map(category => (
                          <div key={category} className="mb-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                              <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded mr-2 text-sm">
                                {category}
                              </span>
                              <span className="text-sm text-gray-500">
                                ({aggregatedMaterials[category].length} items)
                              </span>
                            </h3>

                            <div className="overflow-hidden rounded-lg border border-gray-200">
                              <table className="w-full">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Material</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {aggregatedMaterials[category].map((material, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                        {material.name}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                                        {material.quantity.toLocaleString()}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-600">
                                        {material.unit}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-600">
                                        ₹{material.rate.toLocaleString()}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                                        ₹{material.total.toLocaleString()}
                                      </td>
                                    </tr>
                                  ))}
                                  <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                                    <td colSpan="4" className="px-4 py-3 text-sm text-right text-gray-900">
                                      {category} Subtotal:
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right text-gray-900">
                                      ₹{aggregatedMaterials[category].reduce((sum, m) => sum + m.total, 0).toLocaleString()}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}

                        {/* Grand Total */}
                        <div className="mt-6 p-4 bg-gray-50 rounded-lg border-2 border-gray-300">
                          <div className="flex items-center justify-between">
                            <span className="text-lg font-bold text-gray-900">Grand Total:</span>
                            <span className="text-2xl font-bold text-blue-600">
                              ₹{grandTotal.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </>
                    );
                  })()
                ) : (
                  (() => {
                    const manpowerData = aggregatedManpower;
                    const categories = Object.keys(manpowerData.byCategory);
                    const grandTotal = manpowerData.totals.reduce((sum, labour) => sum + labour.total, 0);
                    const totalManDays = manpowerData.totals.reduce((sum, labour) => sum + labour.days, 0);

                    if (!manpowerData.hasData) {
                      return (
                        <div className="text-center py-12">
                          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Manpower Data Available</h3>
                          <p className="text-gray-600">
                            Manpower requirements could not be calculated for the selected tasks.
                          </p>
                          <p className="text-gray-500 text-sm mt-2">
                            This may be because the task categories don't match the manpower constants.
                          </p>
                        </div>
                      );
                    }

                    return (
                      <>
                        {/* Project Info */}
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Project:</span>
                              <span className="ml-2 font-medium text-gray-900">{projects.find(p => p.id === selectedProjectId)?.name || 'Project'} {breadcrumb.length > 1 ? `- ${breadcrumb[breadcrumb.length - 1]}` : ''}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Date:</span>
                              <span className="ml-2 font-medium text-gray-900">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Building:</span>
                              <span className="ml-2 font-medium text-gray-900">{selectedBuilding}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Tasks:</span>
                              <span className="ml-2 font-medium text-gray-900">{selectedTasks.length} tasks</span>
                            </div>
                          </div>
                        </div>

                        {/* Total Man-days Summary */}
                        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-blue-900">Total Man-days Required</h4>
                              <p className="text-sm text-blue-700 mt-1">Across all labor types</p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-blue-900">{totalManDays.toFixed(1)} days</div>
                              <div className="text-sm text-blue-700">≈ {Math.ceil(totalManDays / 20)} workers/month</div>
                            </div>
                          </div>
                        </div>

                        {/* Manpower by Category */}
                        {categories.map(category => (
                          <div key={category} className="mb-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                              <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded mr-2 text-sm">
                                {category}
                              </span>
                              <span className="text-sm text-gray-500">
                                ({manpowerData.byCategory[category].length} labor types)
                              </span>
                            </h3>

                            <div className="overflow-hidden rounded-lg border border-gray-200">
                              <table className="w-full">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Labour Type</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Man-days</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Daily Rate</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {manpowerData.byCategory[category].map((labour, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                        {labour.labour}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                                        {labour.days.toFixed(2)}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-600">
                                        Days
                                      </td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-600">
                                        ₹{labour.rate.toLocaleString()}/day
                                      </td>
                                      <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                                        ₹{Math.round(labour.total).toLocaleString()}
                                      </td>
                                    </tr>
                                  ))}
                                  <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                                    <td colSpan="4" className="px-4 py-3 text-sm text-right text-gray-900">
                                      {category} Subtotal:
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right text-gray-900">
                                      ₹{Math.round(manpowerData.byCategory[category].reduce((sum, l) => sum + l.total, 0)).toLocaleString()}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}

                        {/* Overall Summary by Labor Type */}
                        <div className="mb-6">
                          <h3 className="text-lg font-bold text-gray-900 mb-3">Overall Summary by Labor Type</h3>
                          <div className="overflow-hidden rounded-lg border border-gray-200">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Labour Type</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Man-days</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Daily Rate</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Workers/Month</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {manpowerData.totals.map((labour, idx) => (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                      {labour.labour}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                                      {labour.days.toFixed(2)} days
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right text-gray-600">
                                      ₹{labour.rate.toLocaleString()}/day
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                                      ₹{Math.round(labour.total).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right text-blue-600">
                                      ≈ {Math.ceil(labour.days / 20)} workers
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Grand Total */}
                        <div className="mt-6 p-4 bg-gray-50 rounded-lg border-2 border-gray-300">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-lg font-bold text-gray-900">Total Labor Cost:</div>
                              <div className="text-sm text-gray-600 mt-1">
                                {totalManDays.toFixed(1)} man-days across {manpowerData.totals.length} labor types
                              </div>
                            </div>
                            <span className="text-2xl font-bold text-blue-600">
                              ₹{Math.round(grandTotal).toLocaleString()}
                            </span>
                          </div>
                        </div>

                        {/* Info Note */}
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-xs text-blue-800">
                            <strong>Note:</strong> Manpower calculated using industry-standard constants.
                            Man-days = Quantity × Labor Constant. Rates configurable in settings.
                          </p>
                        </div>
                      </>
                    );
                  })()
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <button
                  onClick={() => setShowMaterialsModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300"
                >
                  Close
                </button>
                <div className="flex space-x-2">
                  <button className="flex items-center space-x-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    <FileText className="w-4 h-4" />
                    <span>Export PDF</span>
                  </button>
                  <button className="flex items-center space-x-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    <FileText className="w-4 h-4" />
                    <span>Export Excel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Smart Planning Assistant Component */}
      <SmartPlanningAssistant
        isOpen={showSmartPlanningModal}
        onClose={() => setShowSmartPlanningModal(false)}
        masterPlan={masterPlan}
        onAddActivities={(tasks) => {
          setMonthlyPlan(prev => [...prev, ...tasks]);
          setShowSmartPlanningModal(false);
        }}
      />

      {/* Resource Timeline Gantt View */}
      <ResourceGanttView
        isOpen={showResourceTimelineModal}
        onClose={() => setShowResourceTimelineModal(false)}
        tasks={masterPlan}
        projectName={projects.find(p => p.id === selectedProjectId)?.name || 'Project'}
        manpowerConstants={manpowerConstants}
      />

      {/* Resource Planner Modal (Task-First / Resource-First) */}
      <ResourcePlannerModal
        isOpen={showResourcePlannerModal}
        onClose={() => setShowResourcePlannerModal(false)}
        tasks={masterPlan}
        projectId={selectedProjectId || ''}
        projectName={projects.find(p => p.id === selectedProjectId)?.name || 'Project'}
      />
      {/* Team Management Modal */}
      {selectedProjectId && (
        <TeamManagementModal
          isOpen={showTeamModal}
          onClose={() => setShowTeamModal(false)}
          project={projects.find(p => p.id === selectedProjectId) || projects[0]}
          teamMembers={teamMembers}
          onTeamUpdate={refreshTeamData}
        />
      )}

      {/* Monthly Plans Modal */}
      {showMonthlyPlansModal && selectedProjectId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FolderKanban className="w-5 h-5 text-purple-600" />
                Plan Management
              </h2>
              <button
                onClick={() => setShowMonthlyPlansModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <MonthlyPlansView
                projectId={selectedProjectId}
                onSelectPlan={(plan) => {
                  console.log('Selected plan:', plan);
                  // TODO: Load plan tasks into the main view
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Create Weekly Plan Modal */}
      {showCreateWeeklyModal && selectedMonthlyPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-green-600" />
                Create Weekly Plan
              </h3>
              <button
                onClick={() => setShowCreateWeeklyModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                Creating week for <strong>{MONTHS[selectedMonthlyPlan.month - 1]} {selectedMonthlyPlan.year}</strong>
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Week Number</label>
                <select
                  value={newWeeklyPlan.week_number}
                  onChange={(e) => setNewWeeklyPlan(prev => ({ ...prev, week_number: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  {[1, 2, 3, 4, 5, 6].map(num => (
                    <option key={num} value={num} disabled={weeklyPlans.some(w => w.week_number === num)}>
                      Week {num} {weeklyPlans.some(w => w.week_number === num) ? '(exists)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={newWeeklyPlan.start_date}
                    onChange={(e) => {
                      const startDate = new Date(e.target.value);
                      const endDate = new Date(startDate);
                      endDate.setDate(endDate.getDate() + 6);
                      setNewWeeklyPlan(prev => ({
                        ...prev,
                        start_date: e.target.value,
                        end_date: endDate.toISOString().split('T')[0]
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={newWeeklyPlan.end_date}
                    onChange={(e) => setNewWeeklyPlan(prev => ({ ...prev, end_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={newWeeklyPlan.name}
                  onChange={(e) => setNewWeeklyPlan(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={`Week ${newWeeklyPlan.week_number}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateWeeklyModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!selectedMonthlyPlan || !newWeeklyPlan.start_date || !newWeeklyPlan.end_date) return;
                  
                  try {
                    setIsCreatingWeekly(true);
                    await api.createWeeklyPlan({
                      monthly_plan_id: selectedMonthlyPlan.id,
                      week_number: newWeeklyPlan.week_number,
                      start_date: newWeeklyPlan.start_date,
                      end_date: newWeeklyPlan.end_date,
                      name: newWeeklyPlan.name || `Week ${newWeeklyPlan.week_number}`
                    });
                    
                    // Refresh weekly plans (both scoped + project-wide, so the
                    // main dropdown's virtual/real merge picks up the new row).
                    const weeks = await api.getWeeklyPlans(selectedMonthlyPlan.id);
                    setWeeklyPlans(weeks);
                    await refreshProjectWeeklyPlans();

                    const newWeek = weeks.find(w => w.week_number === newWeeklyPlan.week_number);
                    if (newWeek) {
                      setSelectedWeeklyPlan(newWeek);
                    }
                    
                    setShowCreateWeeklyModal(false);
                  } catch (err: any) {
                    alert(err.message || 'Failed to create weekly plan');
                  } finally {
                    setIsCreatingWeekly(false);
                  }
                }}
                disabled={isCreatingWeekly || !newWeeklyPlan.start_date || !newWeeklyPlan.end_date}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isCreatingWeekly && <Loader2 className="w-4 h-4 animate-spin" />}
                Create Week
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Concerns Modal */}
      {showConcernsModal && selectedProjectId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-amber-50">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Concerns & Issues
              </h2>
              <button
                onClick={() => setShowConcernsModal(false)}
                className="p-2 hover:bg-amber-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <ConcernsView 
                projectId={selectedProjectId}
                monthlyPlans={monthlyPlans}
                onRaiseConcern={(taskIds) => {
                  setConcernPreSelectedTasks(taskIds || []);
                  setShowRaiseConcernModal(true);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Raise Concern Modal */}
      {selectedProjectId && (
        <RaiseConcernModal
          isOpen={showRaiseConcernModal}
          onClose={() => {
            setShowRaiseConcernModal(false);
            setConcernPreSelectedTasks([]);
          }}
          projectId={selectedProjectId}
          monthlyPlans={monthlyPlans}
          preSelectedTaskIds={concernPreSelectedTasks}
          currentMonthlyPlanId={selectedMonthlyPlan?.id}
          tasks={masterPlan.map(t => ({
            id: typeof t.id === 'string' ? parseInt(t.id) : t.id,
            activity_name: t.taskName,
            category: t.category,
            floor: t.floor,
            unit_name: t.unit,
            progress: t.progress || 0,
            status: t.status
          }))}
          onSuccess={() => {
            // Refresh concerns if modal is open
            setShowRaiseConcernModal(false);
            setConcernPreSelectedTasks([]);
          }}
        />
      )}
    </div>
  );
};

export default ConstructionPlanningInterface;
